<template>
    <div ref="formulateAgGridOuter" :data-type="context.type" :class="[context.classes.element, 'formulate-ag-grid-component']">
        <div v-if="canShowDisplayGrid">
            <div ref="displayGridContainer" class="formulate-ag-grid-component-gridcontainer">
                <ag-grid-vue
                    ref="displayGrid"
                    :style="{ width: displayGridSizeParams.width, height: displayGridSizeParams.height }"
                    class="ag-theme-supplios"
                    :gridOptions="displayGridOptions"
                    :context="displayGridContext"
                    :columnDefs="displayColDefs"
                    :rowData="lastOutsideValue"
                    :domLayout="displayGridSizeParams.domLayout"
                    :popupParent="displayGridPopupParent"
                    @cellDoubleClicked="onCellDoubleClicked"
                    @cellValueChanged="onDisplayGridCellValueChanged"
                    @columnResized="onDisplayGridColumnResized"
                    @grid-ready="onDisplayGridReady"
                    @first-data-rendered="onDisplayGridFirstDataRendered"
                    @grid-size-changed="onDisplayGridSizeChanged"
                ></ag-grid-vue>
                <div class="mt-2 mb-2 d-flex gap-3 align-items-center justify-content-between">
                    <div class="d-flex gap-4 align-items-center justify-content-start">
                        <!-- we do not allow clicking this unless we are 'settled' locally, which corresponds to allowUpdateLocalFromOutside, so disable if NOT allowing update from outside -->
                        <b-button :disabled="editModalShowing || !allowUpdateLocalFromOutside" :variant="isReadOnly ? 'sharp-primary' : 'primary'" size="sm" class="min-width-150" @click="startModalEdit">
                            <b-icon :icon="isReadOnly ? 'arrows-fullscreen' : 'pencil'" class="mr-1"></b-icon>
                            {{ isReadOnly ? 'View Larger' : 'Edit Data' }}
                        </b-button>
                        <div class="special-text font-size-80">
                            {{ currentValue.length }} item{{ currentValue.length === 1 ? '' : 's' }}
                        </div>
                    </div>
                    <div class="d-flex gap-3 align-items-center justify-content-start">
                        <b-button :variant="'subtle-secondary'" size="sm" @click="exportToExcel('display')">
                            <b-icon icon="file-earmark-excel" variant="success" class="mr-1"></b-icon>
                            Export to Excel</b-button>
                        <b-button-group size="sm">
                            <b-button :variant="'subtle-secondary'" size="sm" @click="sizeDisplayGridColumnsToFit">Fit Columns</b-button>
                            <b-button :variant="'subtle-secondary'" size="sm" @click="autoSizeAllDisplayColumns(false)">Auto-Size Columns</b-button>
                        </b-button-group>
                    </div>
                </div>
            </div>
        </div>

        <b-modal
            v-model="editModalShowing"
            :title="fieldLabel"
            :size="modalSize"
            centered
            no-close-on-backdrop
            no-close-on-esc
            scrollable
            hide-footer
            hide-header-close
            content-class="flex-grow-1"
            @show="onEditModalShow"
            @shown="onEditModalShown"
            @hidden="onEditModalHidden"
            @close="onModalHeaderClose"
        >
        <!-- TODO - need to set editGridPopupParent after it shows, but before grid is allowed to show -->
            <div class="w-100 h-100 min-height-400 d-flex flex-column" style="overflow-y: auto;">  <!-- need min-height here to make room for popup editor components -->
                <div ref="editGridContainer" class="w-100 flex-fill d-flex flex-column" style="position: relative; overflow-y: auto;">  <!-- need position relative here since its used for alignment of grid popover things -->
                    <div v-if="canShowEditGrid" class="d-flex flex-column h-100 w-100 formulate-ag-grid-component-gridcontainer">
                        <ag-grid-vue
                            ref="editGrid"
                            :style="{ width: editGridSizeParams.width, height: editGridSizeParams.height }"
                            class="ag-theme-supplios"
                            :gridOptions="localGridOptions"
                            :context="localGridContext"
                            :columnDefs="colDefs"
                            :rowData="providedRowData"
                            :domLayout="editGridSizeParams.domLayout"
                            :popupParent="editGridPopupParent"
                            @cellEditingStarted="onCellEditingStarted"
                            @cellEditingStopped="onCellEditingStopped"
                            @cellDoubleClicked="onCellDoubleClicked"
                            @cellValueChanged="onCellValueChanged"
                            @pasteStart="onPasteStart"
                            @pasteEnd="onPasteEnd"
                            @grid-ready="onGridReady"
                            @first-data-rendered="onFirstDataRendered"
                            @model-updated="onGridModelUpdated"
                            @row-data-changed="onRowDataChanged"
                            @filter-changed="handleFilterChanged"
                            @columnRowGroupChanged="handleColumnRowGroupChanged"
                            @rowGroupOpened="onRowGroupOpened"
                            @columnVisible="onColumnVisible"
                            @columnMoved="onColumnMoved"
                            @columnResized="onColumnResized"
                            @gridColumnsChanged="onGridColumnsChanged"
                            @rowDragEnd="onRowDragEnd"
                        ></ag-grid-vue>  <!-- class="ag-theme-alpine-custom" -->
                        <div class="mt-2 mb-1 d-flex gap-5 align-items-center justify-content-between">
                            <div class="d-flex gap-5 align-items-center justify-content-start">
                                <div v-if="canAddRows" class="d-flex gap-3 align-items-center justify-content-start">
                                    <b-button variant="primary" size="sm" @click="addRowToGrid">{{ addItemText }}</b-button>
                                    <b-dropdown ref="addMultiRowsDropdown" :id="`add-multirows-dropdown-${localUniqueId}`" size="sm" variant="sharp-primary" text="Add Multiple" class="">
                                        <b-dropdown-form @submit.stop.prevent="handleAddMultiRows">
                                            <b-form-group label="Rows" label-cols="5" :label-for="`add-multirows-${localUniqueId}-input`" label-class="special-text font-size-70 text-right">
                                                <b-form-input
                                                    v-model.number="addMultiRowsNumber"
                                                    :id="`add-multirows-${localUniqueId}-input`"
                                                    size="sm"
                                                    number
                                                ></b-form-input>
                                            </b-form-group>
                                            <b-button size="sm" variant="primary" block @click="handleAddMultiRows">Add Rows</b-button>
                                        </b-dropdown-form>
                                    </b-dropdown>
                                </div>
                                <div class="special-text font-size-80">
                                    {{ currentValue.length }} item{{ currentValue.length === 1 ? '' : 's' }}
                                </div>
                                <div v-if="hasMoneyFields && !isReadOnly" class="d-flex align-items-center gap-2">
                                    <div class="special-text font-size-70 text-right text-muted">Default Currency</div>
                                    <b-form-select
                                        v-model="defaultCurrencyCode"
                                        :options="defaultCurrencyOptions"
                                        value-field="value"
                                        text-field="label"
                                        class="max-width-100"
                                        size="sm"
                                    ></b-form-select>
                                </div>
                            </div>
                            <div class="d-flex gap-3 align-items-center justify-content-start">
                                <b-button :variant="'subtle-secondary'" size="sm" @click="exportToExcel('edit')">
                                    <b-icon icon="file-earmark-excel" variant="success" class="mr-1"></b-icon>
                                    Export to Excel</b-button>
                                <b-button-group size="sm">
                                    <b-button :variant="'subtle-secondary'" size="sm" @click="sizeColumnsToFit">Fit Columns</b-button>
                                    <b-button :variant="'subtle-secondary'" size="sm" @click="autoSizeAllColumns(false)">Auto-Size Columns</b-button>
                                </b-button-group>
                            </div>
                            <!-- ADD STUFF BELOW IF NOT SHOWING EDIT GRID IN A MODAL -->
                            <!-- <div v-if="!isReadOnly" class="d-flex gap-4 align-items-center justify-content-between">
                                <span v-if="hasRowErrors" variant="soft-danger" class="chip chip-danger text-uppercase">{{ Object.keys(errorsByLocalId).length }} Invalid Row{{ Object.keys(errorsByLocalId).length === 1 ? '' : 's' }}</span>
                                <span v-if="hasLocalChanges" variant="soft-warning" class="chip chip-warning text-uppercase">Unsaved Changes</span>
                                <b-button :variant="hasLocalChanges ? 'success' : 'subtle-success'" size="sm" class="min-width-100" :disabled="hasRowErrors" @click="saveValueToForm">Save Edits</b-button>
                            </div> -->
                        </div>
                        <div v-if="showDebug" class="">
                            <hr>
                            <div class="container">
                                <div class="row">
                                    <div class="col">
                                        <b-button @click="dumpDiffRows">Dump Diff Rows</b-button>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-6">
                                        <h6>currentPreppedValue</h6>
                                        <pre>{{ currentPreppedValue }}</pre>
                                    </div>
                                    <div class="col-6">
                                        <h6>lastOutsidePreppedValue</h6>
                                        <pre>{{ lastOutsidePreppedValue }}</pre>
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-6">
                                        <h6>currentValue</h6>
                                        <pre>{{ currentValue }}</pre>
                                    </div>
                                    <div class="col-6">
                                        <h6>errorsByLocalId</h6>
                                        <pre>{{ errorsByLocalId }}</pre>
                                    </div>
                                </div>
                                <hr>
                                <div class="row">
                                    <div class="col-6">
                                        <h6>colDefs</h6>
                                        <pre>{{ colDefs }}</pre>
                                    </div>
                                    <div class="col-6">
                                        <h6>child cols</h6>
                                        <pre>{{ cols }}</pre>
                                    </div>
                                </div>
                                <hr>
                                <div class="row">
                                    <div class="col-12">
                                        <h6>context.model</h6>
                                        <pre>{{ context.model }}</pre>
                                    </div>
                                </div>
                            </div>        
                        </div>
                    </div>
                </div>
                <div class="">
                    <hr>
                    <div class="d-flex flex-row align-items-center justify-content-between">
                        <div class="d-flex gap-4 align-items-center justify-content-between">
                            <b-button v-if="!showModalCancelConfirm" variant="secondary" size="md" class="min-width-100" @click="requestModalCancel">{{ isReadOnly ? 'Close' : 'Cancel' }}</b-button>
                            <div v-if="showModalCancelConfirm" class="d-flex gap-3 align-items-center justify-content-between">
                                <div class="text-danger">You have unsaved changes that may be lost if you cancel.  Continue?</div>
                                <b-button variant="danger" size="sm" class="min-width-100" @click="performModalCancel">Yes, Close</b-button>
                                <b-button variant="secondary" size="sm" class="min-width-100" @click="abortModalCancel">No, Continue Editing</b-button>
                            </div>
                        </div>
                        <div v-if="!isReadOnly" class="d-flex gap-4 align-items-center justify-content-between">
                            <span v-if="isCellEditActive" class="chip chip-secondary text-uppercase">Editing</span>
                            <span v-if="hasRowErrors" variant="soft-danger" class="chip chip-danger text-uppercase">{{ Object.keys(errorsByLocalId).length }} Invalid Row{{ Object.keys(errorsByLocalId).length === 1 ? '' : 's' }}</span>
                            <span v-if="hasLocalChanges" variant="soft-warning" class="chip chip-warning text-uppercase">Unsaved Changes</span>
                            <b-button :variant="hasLocalChanges ? 'success' : 'subtle-success'" size="md" class="min-width-100" :disabled="hasRowErrors || isCellEditActive" @click="startSaveToForm">Save & Close</b-button>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import { AgGridVue } from 'ag-grid-vue';
import { nanoid } from 'nanoid';
import { LiveGridCol } from './agGridFormSchema.js';
import { parseFixedDecimal } from '@/js/utils/numberUtils.js';
import {
    AgGridFileRenderer,
    AgGridFileEditor,
} from './AgGridFileComponents.js';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { detailedDiff } from 'deep-object-diff';


const localIdField = '_localId';  // field in each row data object to use as unique ID for that row
const controlColId = '_control';

const isNil = (value) => (value === null || value === undefined);

const ControlCellRenderer = Vue.extend({
    template: `
        <v-popover
            v-if="canRemoveRow"
            offset="8"
            :open.sync="isOpen"
            auto-hide
            :handleResize="false"
            :disabled="!canRemoveRow"
            placement="right"
            popover-inner-class="popover-inner"
        >  
            <!-- This will be the popover target (for the events and position) -->
            <b-link class="text-secondary">
                <b-icon icon="x-circle" variant="danger" scale="1" class=""></b-icon>
                <!-- <b-iconstack font-scale="1.5" class="tooltip-target">
                    <b-icon stacked icon="three-dots" variant="secondary" scale="0.75" class=""></b-icon>
                    <b-icon stacked icon="circle" variant="danger" class=""></b-icon>
                </b-iconstack> -->
            </b-link>
        
            <!-- This will be the content of the popover -->
            <template slot="popover" class="p-0">
                <div v-if="showConfirmDelete" key="confirmDelete" class="py-2 px-3">
                    <div class="font-weight-bold mb-1 text-center">Delete Item</div>
                    <div class="mb-2 text-center">
                        <div>Are you sure?</div>
                    </div>
                    <b-button size="sm" block variant="sharp-secondary" @click="cancelDelete">No, Cancel</b-button>
                    <b-button size="sm" block variant="danger" @click="requestDelete">Yes, Delete</b-button>
                </div>
            </template>
        </v-popover>
    `,
    data() {
        return {
            isOpen: false,
            showConfirmDelete: true,  // always showing now... only use this if you'll have swappable content in the popover
            isEditAllowed: true,  // make dynamic later, set in mounted?
            // NOTE: this was originally created to hold only one "action" (delete) -- will need to refactor if/when you want to add more actions
        };
    },
    computed: {
        localId() {
            return this.params.data ? this.params.data[localIdField] : null;
        },
        canRemoveRow() {
            return this.isEditAllowed && this.params.context.componentParent && this.params.context.componentParent.canRemoveRows;  // from computed on parent component
        },
    },
    methods: {
        requestDelete() {
            this.closeAndReset();  // close popover and reset... before parent deletes us
            if (!this.canRemoveRow) {
                return;  // just in case, should not even be able to click if can't remove
            }
            if (this.params.context.componentParent && this.localId) {
                this.params.context.componentParent.deleteRow(this.localId);
            }
        },
        cancelDelete() {  // just close and reset
            this.closeAndReset();
        },
        closeAndReset() {
            // DRY function to close popover & reset all things (in that order, so it doesn't flash other things before closing)
            // need to reset things because component doesn't get destroyed for some time after closing
            this.isOpen = false;
            // this.showConfirmDelete = false;   // only set this if you have swappable content
        }
    },
});

/* <b-form-radio-group
                :id="uniqueId"
                :checked="localValue"
                @input="handleSelection"
                :options="localOptions"
                buttons
            ></b-form-radio-group> */

const BooleanCellEditor = Vue.extend({
    template: `
        <div :ref="'container'" class="d-flex align-items-center justify-content-center" :style="containerStyles" tabindex="0" @keydown="onKeyDown">
            <b-button-toolbar :id="uniqueId" :ref="'buttonToolbar'" key-nav :aria-label="'Selector for ' + fieldName">
                <b-button-group size="sm" class="mx-1">
                    <b-button :variant="localValue === true ? 'primary' : 'subtle-primary'" @click="handleSelection(true)">{{trueLabel}}</b-button>
                    <b-button :variant="localValue === false ? 'primary' : 'subtle-primary'" @click="handleSelection(false)">{{falseLabel}}</b-button>
                </b-button-group>
            </b-button-toolbar>
        </div>
    `,
    data() {
        return {
            localValue: null,  // also allow null?
            parentCellElement: null,  // in mounted we stash reference to the eGridCell (HTML element for the cell we're in)
        };
    },
    computed: {
        fieldName() {
            return this.params.colDef?.field || '';
        },
        nodeId() {
            return this.params.node?.id || '';
        },
        uniqueId() {
            return `cell-booledit-${this.fieldName}-${this.nodeId}`;
        },
        localOptions() {  // TODO - get custom labels for true/false
            return [
                { text: this.trueLabel, value: true },
                { text: this.falseLabel, value: false },
            ];
        },
        trueLabel() {
            return this.params.trueLabel || 'Yes';
        },
        falseLabel() {
            return this.params.falseLabel || 'No';
        },
        parentCellWidth() {
            return this.parentCellElement ? this.parentCellElement.offsetWidth : 0;
        },
        parentCellHeight() {
            return this.parentCellElement ? this.parentCellElement.offsetHeight : 0;
        },
        containerStyles() {
            return {
                minWidth: this.parentCellWidth + 'px',
                minHeight: this.parentCellHeight + 'px'
            };
        }
    },
    methods: {
        getValue() {
            return this.localValue;
        },
        isPopup() { // might want popup in case things are too wide, but if so, make sure it covers the entire cell
            return true;
        },
        setLocalValue(boolValue) {
            if (boolValue === undefined) {
                boolValue = null;  // allow null, and coerce undefined to null
            }
            this.localValue = boolValue;  
        },
        toggleValue() {
            if (isNil(this.localValue)) {
                return;  // do nothing if null (not yet set)
            }
            this.setLocalValue(!this.localValue);
        },
        handleSelection(boolValue) {
            console.log('BooleanCellEditor - handleSelection - user selected:', boolValue);
            this.setLocalValue(boolValue);
            this.params.api.stopEditing();
        },
        onKeyDown(event) {  // your b-button-toolbar might already handle this...
            if (this.localValue === null) {
                // if localValue is null, we don't know what to do for left/right, so just abort
                return;
            }
            let key = event.which || event.keyCode;
            if (
                key == 37 || // left
                key == 39  // right
            ) {
                this.toggleValue();
                event.stopPropagation();
            }
        },
    },
    created() {
        this.setLocalValue(this.params.value);
    },
    mounted() {
        Vue.nextTick(() => {
            this.$refs.buttonToolbar.$el.focus();
            console.log('BooleanCellEditor - mounted - dumping params', this.params);
        });
        this.parentCellElement = this.params.eGridCell;  // eGridCell is the HTMLElement for the cell we're in
    },
});

const BootstrapVueDatePickerEditor = Vue.extend({  // TODO - copied from other place... might need work!
    template: `
        <div class="datepicker-cell-wrapper">
            <b-calendar 
                ref="calComp"
                v-model="dateValue" 
                @input="setDate"
                @context="onCalendarContext" 
                value-as-date
                locale="en-US"
            >
                <div class="d-flex" dir="ltr">
                    <b-button
                        size="sm"
                        variant="minimal-primary"
                        v-if="dateValue && !params.hideClear"
                        class="button-text"
                        @click="clearDate"
                    >
                        Clear
                    </b-button>
                </div>
            </b-calendar>
        </div>
    `,
    data() {
        return {
            emptyValue: null,  // keep it as null for empty value
            dateValue: null,  // if value-as-date=true, then we use JS Date, otherwise localValue in YYYY-MM-DD format - BUT you need to actually set the data to null if its empty - backend wants that
            context: null,  // the full context object when its updated, lots of stuff
            // TODO - pass in params for allowing only FUTURE dates?
            // if so, make sure you adjust date to TENANT TZ date
            // you can pass in 'hideClear' as extra param (boolean)
        }
    },
    created() {
        this.dateValue = this.params.value || this.emptyValue;
        // for editors, just work with local data, then use this.params.api.stopEditing() to tell grid you're done, then use getValue() method to tell grid where to get value
    },
    mounted() {
        // console.log('params from bsv datepicker component', this.params);
        this.$nextTick(() => {
            const calComp = this.$refs.calComp;  
            if (calComp) {
                calComp.focus();  // might have to get its element?
            }
        });
    },
    methods: {
        getValue() {  // if this is an EDITOR, this is main method that grid uses to get the value
            //console.log('from getValue in date picker component, dateValue is', this.dateValue);
            return this.dateValue || this.emptyValue;
        },
        isPopup() {
            return true;
        },
        setDate: function(newDate) {  // set new date value and stop editing
            this.dateValue = newDate || this.emptyValue;
            this.params.api.stopEditing();  // this tells grid we're done and it gets value using getValue() - can also use this.params.stopEditing()
            // try returning focus to the grid cell
            this.params.eGridCell.focus();  // eGridCell is the ag-grid cell we're in, it is HTMLElement
        },
        onCalendarContext(ctx) {
            // handles context event, which is fired whenever something changes
            // v-model is updated separately
            this.context = ctx
        },
        clearDate() {
            this.dateValue = this.emptyValue;
        }
    }
});

const CustomTextAreaEditor = Vue.extend({
    // we have to make this one because the default ag-grid textarea editor does not handle the initial charPress value to populate the textarea!
    template: `
        <div class="custom-textarea-editor-wrapper p-2" :style="{ width: width, minHeight: '60px' }">
            <b-form-textarea
                :value="localValue"
                @input="setValue"
                :placeholder="params.placeholder || ''"
                :rows="rows"
                :state="localValueIsValid ? null : false"
                @keydown.enter="handleEnter"
                @keydown.esc.stop.prevent="cancelEditing"
                ref="textarea"
            ></b-form-textarea>
            <div>
                <div v-if="maxLength" class="text-right mt-1 font-size-70" :class="currentLength > maxLength ? 'text-danger' : 'text-muted'">
                    {{ currentLength }} / {{ maxLength }}
                </div>
            </div>
        </div>
    `,
    data() {
        return {
            // we cannot set these to this.params.value -- must do it in created, even though it seems to work in other components...
            localValue: '',
            validValue: '',  // we only set this if the value is valid (not beyond max length, etc)
        }
    },
    computed: {
        // we make this at least compatible with the default ag-grid textarea editor, which expects params of { maxLength, rows, cols, placeholder? } -- but you can also pass in your own custom params as needed
        // we make computeds for them just to set defaults, etc.
        // we don't use cols though... but we have 'width' instead
        currentLength() {
            return this.localValue.length;
        },
        maxLength() {
            return this.params.maxLength || undefined;  // default max length if not provided
        },
        rows() {
            return this.params.rows || 3;  // default to 3 rows if not provided
        },
        width() {
            const rawWidth = this.params.width || 300;  // use provided width, or parent cell width, or default to 300px
            const finalWidth = typeof rawWidth === 'number' ? `${rawWidth}px` : rawWidth;
            return finalWidth;
        },
        localValueIsValid() {
            // you only have maxLength for now, but if you have more rules via params, check those here
            // should we check our rules from the LiveCol?  pass that in somehow?
            if (this.maxLength && this.localValue.length > this.maxLength) {
                return false;
            }
            return true;
        },
    },
    created() {
        console.log('CustomTextAreaEditor - created - params are', this.params);
        this.setValue(this.params.value || '');  // set initial value from params, or empty string if not provided (also updates validValue)
        const charPress = this.params.charPress;
        console.log('CustomTextAreaEditor - created - charPress is', charPress);
        if (charPress) {
            // check if its a printable character (not control key, backspace, etc) before setting value?
            this.setValue(charPress);
        }
    },
    mounted() {
        this.$nextTick(() => {
            const textarea = this.$refs.textarea;
            if (textarea) {
                textarea.focus();
            }
        });
    },
    methods: {
        getValue() {
            return this.validValue;
        },
        isPopup() {
            return true;
        },
        setValue(newValue) {
            this.localValue = newValue || '';
            if (this.maxLength && newValue.length > this.maxLength) {
                // if beyond the maxLength, we just don't set the localValue
                // TODO - if you add more rules, need to check them here... just call a validate function from the LiveCol?
                // ...or, just let this set the invalid value, but then grid row validation will fail... that might be OK
                console.log(`CustomTextAreaEditor - setValue - value is beyond maxLength of ${this.maxLength}, not updating validValue`);
                // truncate???  maybe that is safer?
                this.validValue = newValue.substring(0, this.maxLength);
            } else {
                // otherwise, we update validValue to the new value
                this.validValue = this.localValue;
            }
        },
        handleEnter(event) {
            console.log('CustomTextAreaEditor - handleEnter - event is', event);
            // we want to look at shift-enter vs. enter
            if (event.shiftKey) {
                // if shift-enter, we insert a newline at the current cursor position - and stop/prevent
                event.stopPropagation();
                event.preventDefault();
                const textarea = this.$refs.textarea;
                const startPos = textarea.selectionStart;
                const endPos = textarea.selectionEnd;
                const newValue = this.localValue.substring(0, startPos) + '\n' + this.localValue.substring(endPos);
                this.setValue(newValue);
                // after updating value, we need to set the cursor position to after the newline we just inserted
                this.$nextTick(() => {
                    textarea.selectionStart = textarea.selectionEnd = startPos + 1;
                });
            } else {
                // NOT PUTTING ANYTHING HERE -- just let the event propagate up so the grid handles it normally
            }
        },
        finishEditing() {
            this.params.api.stopEditing();
            this.params.eGridCell.focus();
        },
        cancelEditing() {
            // just stop editing without changing value
            this.params.api.stopEditing(true);  // passing true tells it to cancel and not save changes
            this.params.eGridCell.focus();
        }
    },
});

const MultiSelectCellEditor = Vue.extend({
    template: `
        <div ref="multiSelectEditorOuter" tabindex="0" class="multiselect-cell-wrapper p-2 font-size-80">
            <b-form-group
                :label="colLabel"
                v-slot="{ ariaDescribedby }"
                class="mb-0"
                style="max-height: 400px; overflow-y: auto; max-width: 200px;"
                label-class="font-weight-700 mb-1"
            >
                <b-form-checkbox-group
                    ref="theCheckboxGroup"
                    :checked="selected"
                    @input="setSelected"
                    :options="options"
                    :aria-describedby="ariaDescribedby"
                    stacked
                ></b-form-checkbox-group>
            </b-form-group>
            <div class="mt-2 d-flex justify-content-between align-items-center">
                <div class="font-weight-bold">{{ selectedCountText }}</div>
                <b-button size="sm" variant="sharp-primary" class="button-text" @click="finishEditing">Done</b-button>
            </div>
        </div>
    `,
    data() {
        return {
            // emptyValue: null,  // keep it as null for empty value
            selected: [],  // local selection
        }
    },
    // NOTE: we pass in these special params in colDef: { optionsObject: {key: label, ...} } -- that is our standard way of doing options in ag-grid, but you might actually want different structure here for showing disabled ones differently?
    computed: {
        options() {
            // we provide optionsObject as param in cellEditorParams
            const optObj = this.params.optionsObject || {};
            return Object.entries(optObj).map(([value, label]) => ({ value, text: label }));
        },
        colLabel() {
            return this.params.colDef ? this.params.colDef.headerName : '';
        },
        totalOptionsCount() {
            return this.options.length;
        },
        selectedCountText() {
            const count = this.selected.length;
            // return `${count} item${count === 1 ? '' : 's'} selected`;
            return `${count} of ${this.options.length}`;
        },
    },
    created() {
        this.selected = this.params.value || [];
        // for editors, just work with local data, then use this.params.api.stopEditing() to tell grid you're done, then use getValue() method to tell grid where to get value
    },
    mounted() {
        // console.log('params from bsv datepicker component', this.params);
        this.$nextTick(() => {
            // need to do this stuff to make all the tabbing/keydown stuff nice
            const focusRef = this.$refs.multiSelectEditorOuter;  
            if (focusRef && focusRef.focus) {
                console.log('focusing multi select editor', focusRef);
                focusRef.focus();  // might have to get its element?
            }
        });
    },
    methods: {
        getValue() {  // if this is an EDITOR, this is main method that grid uses to get the value
            //console.log('from getValue in date picker component, dateValue is', this.dateValue);
            return this.selected || [];
        },
        isPopup() {
            return true;
        },
        setSelected: function(newValue) {
            this.selected = newValue || [];
        },
        finishEditing() {
            this.params.api.stopEditing();  // this tells grid we're done and it gets value using getValue() - can also use this.params.stopEditing()
            this.params.eGridCell.focus();  // eGridCell is the ag-grid cell we're in, it is HTMLElement
        },
        previousNextCell(prevNext) {
            // should we call finishEditing here???
            if (prevNext === 'previous') {
                this.params.api.tabToPreviousCell();
            } else if (prevNext === 'next') {
                this.params.api.tabToNextCell();
            }
        },
    }
});

const MoneyCellEditor = Vue.extend({
    template: `
        <div class="d-flex justify-content-center align-items-center" :style="{ minWidth: '140px', height: editorHeight }">
            <div class="d-flex justify-content-center align-items-center" :style="{ minWidth: '140px', height: editorHeight, maxHeight: '40px' }">
                <select 
                    :value="localCurrency"
                    @input="updateCurrency"
                    class="h-100 border-0 flex-fill"
                    :class="hasCurrency ? '' : 'text-danger'"
                    style="minWidth: 60px;"
                    @keydown.tab.prevent.stop="focusAmountInput"
                    @keydown.shift.tab.prevent.stop="previousNextCell('previous')"
                    ref="currencyInput"
                >
                    <option 
                        v-for="currObj in currencyOptionsObjects" 
                        :key="currObj.value"
                        :value="currObj.value"
                    >{{ currObj.label }}</option>
                </select>
                <input 
                    type="number" 
                    :value="localAmountStr"
                    @input="updateAmount"
                    @keydown.shift.tab.prevent.stop="focusCurrencyInput"
                    step="1"
                    class="h-100 border-0 py-0 pr-0 pl-1 flex-fill"
                    style="minWidth: 80px;"
                    ref="amountInput"
                />
            </div>
        </div>
    `,  // TODO - fix it so you can keep currency (at least locally) if you enter that but no amount
    // NOTE: we have this as isPopup=true so we are not fighting with the grid size limits -- it will kinda float over the cell
    data() {
        return {
            localCurrency: null,
            localAmount: null,  // the number, or null if empty
            localAmountStr: '',  // string version of amount, to allow user to type in things like "12." or "0.0" without it getting messed up
            localAmountIsValid: true,  // set to false if user types in something invalid
            currentCellHeight: null,  // set this in created so we try to match the row height for better UI
        }
    },
    computed: {
        parentComponent() {
            return this.params.context.componentParent;
        },
        liveGridCol() {
            return this.parentComponent.liveGridColsByField[this.params.colDef.field];
        },
        currencyOptions() {  // array of ONLY THE CURRENCY CODES
            // we provide currencyOptions as param in cellEditorParams
            return this.params.currencyOptions || [];  // this.liveGridCol.getCurrencyOptions() || [];
        },
        currencyOptionsObjects() {
            // return array of currency options as OBJECTS with {value, label}, with a placeholder option at start if we don't have a single currency
            if (this.currencyOptions.length === 1) {
                return this.currencyOptions.map(code => ({value: code, label: code}));
            }
            return [{value: null, label: '---'}, ...this.currencyOptions.map(code => ({value: code, label: code}))];
        },
        decimalPlaces() {  // provided in cellEditorParams
            return this.params.decimalPlaces;  // this.liveGridCol.getDecimalPlaces();
        },
        singleCurrency() {
            // return the single currency code if we only have one option, otherwise null
            return this.currencyOptions.length === 1 ? this.currencyOptions[0] : null;
        },
        hasCurrency() {
            return !!this.localCurrency;
        },
        editorHeight() {
            // we want to try to match the row height if we can, for better UI, but if not, just use auto
            // but cells can also get super tall because of text wrapping, so we also set a max height in CSS
            return this.currentCellHeight ? this.currentCellHeight + 'px' : 'auto';
        }
    },
    created() {
        console.log('MoneyCellEditor - created - params are', this.params);
        // set the localValueObj from params.value if we have it
        this.setInitialValue(this.params.value, this.params.charPress);
        const cellElement = this.params.eGridCell;
        if (cellElement) {
            this.currentCellHeight = cellElement.offsetHeight;
            console.log('MoneyCellEditor - created - cell height is', this.currentCellHeight);
        }
    },
    mounted: function() {
        Vue.nextTick(() => {
            // we already set initial value (and defaulted to singleCurrency if possible) in created hook
            if (this.hasCurrency || this.params.charPress) {
                // even if no currency, if user started typing, focus amount
                this.$refs.amountInput.focus();
            } else {
                this.$refs.currencyInput.focus();
            }
        });  // must wrap it in nextTick or it doesn't focus
    },
    methods: {
        getDefaultCurrencyFromParent() {
            // get default currency from parent component (the main FormulateAgGrid component) --- IF SET!
            // only call this if you don't have singleCurrency and it is otherwise unknown/unset locally
            if (this.parentComponent && this.parentComponent.defaultCurrencyCode) {
                const defaultCurrCode = this.parentComponent.defaultCurrencyCode;
                return this.currencyOptions.includes(defaultCurrCode) ? defaultCurrCode : null;
            }
            return null;
        },
        setInitialValue(initialValueRaw, charPress) {  // call this during created hook with setInitialValue(params.value, params.charPress) 
            // no need to return anything... just set this.localValueObj
            // if we have charPress, we assume that will be for the amount part -- only printable characters will be in charPress
            let initialAmount = null;
            let initialCurrency = null;
            let initialAmountStr = ''; // set this as we go, because how if/how we format will depend on the source of the initial value
            const defaultCurrency = this.singleCurrency || this.getDefaultCurrencyFromParent() || null;  // the default currency to use if none provided, IN THIS ORDER OF PRIORITY  (this.singleCurrency will be null if we don't have exactly one)
            if (!initialValueRaw) {  // totally empty?
                initialAmount = null;
                initialCurrency = defaultCurrency;  // will be null if we don't have exactly one option for currency
                initialAmountStr = '';  // empty string
            } else if (['string', 'number'].includes(typeof initialValueRaw)) {  // just got a string or number?  should not happen, but....
                initialAmount = parseFixedDecimal(initialValueRaw, this.decimalPlaces);
                initialCurrency = defaultCurrency;  // will be null if we don't have exactly one option for currency
                initialAmountStr = this.formatNumberToString(initialAmount);  // we format it WITH fixed decimal places since this was based on existing input, not just entered by user
            } else if (typeof initialValueRaw === 'object' && initialValueRaw !== null) {
                // if its an object, try to get amount and currency from it.. those take priority, but for currency, set it to the singleCurrency if we have one
                initialAmount = initialValueRaw.amount ? parseFixedDecimal(initialValueRaw.amount, this.decimalPlaces) : null;
                initialCurrency = initialValueRaw.currency || defaultCurrency;  // will be null if we don't have exactly one option
                initialAmountStr = this.formatNumberToString(initialAmount);  // we format it WITH fixed decimal places since this was based on existing input, not just entered by user
            }

            // check/set charPress AFTER initial value, because any new charPress should take priority for setting amount, and we want to keep same currency if there was any initial value
            if (charPress) {  // if you have something in charPress, that means user wants to edit it, so use this value for amount instead of any existing amount value
                console.log(`MoneyCellEditor - charPress: ${charPress}`);
                if (Number.isFinite(Number(charPress))) {  // check if charPress is a valid number
                    initialAmount = parseFixedDecimal(charPress, this.decimalPlaces);
                    initialAmountStr = charPress;  // just use the string version of what they typed
                }
            }
            this.localAmount = initialAmount;
            this.localCurrency = initialCurrency;
            // this.localAmountStr = this.formatNumberToString(this.localAmount);  // NO -- if use hits '4' then '4.00' will first appear, which is not cool
            // this.localAmountStr = initialAmount !== null ? String(initialAmount) : '';  // just use string version of number, or '' if null
            this.localAmountStr = initialAmountStr;  // use the string version we set above
            this.localAmountIsValid = true;  // might be null, but its valid
        },
        getValue() {
            // this is the main method that grid uses to get the value from this editor
            // we use isCancelAfterEnd to tell grid if the value is valid or not, so here we just return the value
            // if (isNil(this.localValueObj.amount) || !this.localValueObj.currency) {
            if (isNil(this.localAmount)) {
                return null;  // we will try to save the currency if its set, even if amount is null... otherwise it could be annoying...
            } else {
                const finalValue = {amount: this.localAmount, currency: this.localCurrency};
                console.log('MoneyCellEditor - getValue returning:', finalValue);
                return finalValue;  // return a copy of the object
            }
        },
        formatNumberToString(numberValue) {
            // format this.localAmount to string with fixed decimal places, or '' if null
            // don't call this on each keystroke... will mess up user input I think
            if (numberValue === null) {
                return '';
            }
            return numberValue.toFixed(this.decimalPlaces);  // might want to use toLocaleString later
        },
        isLocalValueValid() {
            // combines multiple things into one check
            // is the amount valid on its own, and do we have both amount and currency if either is set
            if (isNil(this.localAmount) && !this.localCurrency) {
                console.log('MoneyCellEditor - isLocalValueValid - both amount and currency empty - valid');
                return true;  // BOTH empty is valid
            }
            if (isNil(this.localAmount) || !this.localCurrency) {
                console.log('MoneyCellEditor - isLocalValueValid - one of amount or currency empty - INVALID');
                return false;  // if both are not empty (above) -- check if either is empty, the other is not -- invalid
            }
            const amountValid = this.localAmountIsValid;  // if the amount/currency combo is OK, then just return based on amount validity
            console.log('MoneyCellEditor - isLocalValueValid - amount and currency both set - amount validity is', amountValid);
            return amountValid;
        },
        // Gets called once before editing starts, to give editor a chance to
        // cancel the editing before it even starts.
        isCancelBeforeStart() {
            return false;
        },
        isCancelAfterEnd() {
            // Gets called once when editing is finished (eg if Enter is pressed).
            // If you return true, then the result of the edit will be ignored.
            return this.localAmountIsValid ? false : true;  // if valid, return false (meaning don't cancel), if invalid, return true (meaning cancel the edit)
            // we were previously only aborting the edit if the amount was invalid, but now we are also doing that if amount is not set -- that is more annoying for data entry, but safer overall!
            // we can still only look at the amount, as long as we visually show errors for missing currency and prevent submission in that case, which is what we're doing now
            // return this.isLocalValueValid() ? false : true;  // if valid, return false (meaning don't cancel), if invalid, return true (meaning cancel the edit)
        },
        isPopup() {
            return true;  // we want this to be a popup editor, so it doesn't mess with the layout of the grid when it appears
        },
        // handle inputs
        updateCurrency(event) {
            console.log('raw event in MoneyCellEditor updateCurrency:', event);
            this.localCurrency = event.target.value;
            console.log('MoneyCellEditor - updateCurrency - new currency:', this.localCurrency);
            // do something to notify parent that it should try getting value???
        },
        updateAmount(event) {
            // we do NOT use parseFixedDecimal here, we will do that in getValue
            // otherwise we'll get weird rounding going on as the user types... really bad
            const rawAmount = event.target.value;
            this.localAmountStr = rawAmount;  // keep the string version updated -- or only set it if we successfully parse it to number???
            this.localAmount = parseFixedDecimal(rawAmount, this.decimalPlaces);  // set this here???
            this.localAmountIsValid = this.localAmount === null && rawAmount !== '' ? false : true;  // if we got null back, and user didn't just clear it out, that means invalid input
            console.log('MoneyCellEditor - updateAmount - new localAmountStr:', this.localAmountStr);
        },
        focusAmountInput() {
            this.$refs.amountInput.focus();
        },
        focusCurrencyInput() {
            this.$refs.currencyInput.focus();
        },
        previousNextCell(prevNext) {
            if (prevNext === 'previous') {
                this.params.api.tabToPreviousCell();
            } else if (prevNext === 'next') {
                this.params.api.tabToNextCell();
            }
        },
    },
});


export default {
    name: 'FormulateAgGrid',
    components: {
        AgGridVue,
    },
    mixins: [],
    props: {
        context: {
            // the required Formulate context prop
            type: Object,
            required: true,
        },
        cols: {
            // form item schema for all child columns
            type: Array,
            required: true,
        },
        gridOptions: {
            // additional grid options for ag-grid
            type: Object,
            required: false,
            default: () => ({}),
        },
        gridContext: {
            // context object for ag-grid
            type: Object,
            required: false,
            default: () => ({}),
        },
        // gridSaveBehavior: {  // NOT USING YET
        //     // when to save changes back to form context.model - 'live' (on every cell edit), 'button' (only when user clicks Save Edits button)
        //     type: String,
        //     required: false,
        //     default: 'button',
        //     validator: function(value) {
        //         return ['live', 'button'].includes(value);
        //     },
        // },
        gridHeight: {
            // height of the DISPLAY grid, can be string (CSS value)
            type: [Number, String],
            required: false,
            default: '600px',
        },
        // gridConfig: {  // NOT USING YET -- add to formulate-config.js when you start using
        //     type: Object,
        //     required: false,
        //     default: () => ({}),
        //         // {
        //         //     allowedCurrencies: ['USD', 'EUR', 'GBP'],  // if you have money columns, which currencies to allow
        //         //     defaultCurrency: 'USD',  // if you have money columns, which one to default to if only one
        //         //     allowExcelExport: true,
        //         //     allowRowDrag: true,
        //         //     allowSorting: true,
        //         //     allowFiltering: true,
        //         //     allowColumnResize: true,
        //         //     allowMultiColumnSort: true,
        //         //     showColumnSidebar: true,
        //         // }  // and other on/off or simple config options (defined by us, but "translated" to actual ag-grid options in code)
        // },
        colConfig: {
            // options for column selector (if we implement that) -- which ones to show, pin, defaults?, show or hide overall, etc.   
            // changes here are NOT reactive.... so if you change this from parent, it won't update here... too much complexity for now
            type: Object,
            required: false,
            default: () => ({}),
                // {
                //     pinnedLeft: [],
                //     pinnedRight: [],
                //     enabled: [],
                //     showColumnSelector: false,
                // }
        },
        noAdd: {  // do not use directly, use computed which looks at isReadOnly also (canAddRows)
            // if true, disables ability to add rows, if False, then no specific block (but could be blocked some other way, such as read-only)
            type: Boolean,
            required: false,
            default: false,
        },
        noRemove: { // do not use directly, use computed which looks at isReadOnly also (canRemoveRows)
            // if true, disables ability to remove rows, if False, then no specific block (but could be blocked some other way, such as read-only)
            type: Boolean,
            required: false,
            default: false,
        },
        noReorder: {  // do not use directly, use computed which looks at isReadOnly also (canReorderRows)
            // if true, disables ability to reorder / drag rows, if False, then no specific block (but could be blocked some other way, such as read-only)
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            localUniqueId: `formulate-aggrid-${nanoid()}`,  // unique ID for this instance, used for things like tooltips and aria attributes
            showDebug: false,  // set to true to show debug info at bottom of component
            // VALUE AND CHANGE DETECTION
            loopCounter: 0,  // just a counter we increment whenever we do something that might trigger a loop... to help debug infinite loops
            lastDataFromOutsideRaw: null,  // the last raw data we got from outside (context.model) - used to detect if we got new data from outside - null means we never got any yet
            isChangedFromOutside: false,  // set to true when we detect that context.model changed from outside
            initialRowsRaw: [],  // the raw initial rows passed in from parent, before we add localIds and _isValid
            providedRowData: [],  // the initial data we provide to edit grid... -- it DOES get mutated as the grid changes though.... but we should not count on that... we iterate over rows to get data when needed
            currentValue: [],  // this is the CURRENT value, updated whenever we edit cells or move rows, POSSIBLY WITH SLIGHTLY DIFFERENT REPRESENTATION FROM DATA IN GRID (JSDate to ISO strings, for example)... this is what we save to form
            lastOutsideValue: null,  // we make this for the DISPLAY GRID, to show the last value we got from outside (context.model), converted from 'raw' to add the extra stuff we have locally (like _isValid)
            lastOutsidePreppedValue: null,  // the most recent external/outside provided data, but in our 'prepped' format (with defaults, but no _localId or _isValid)
            currentPreppedValue: null,
            lastOutsidePreppedValueStringified: null,  // null means never set, otherwise holds stringified version of initial value for comparison ('prepped/extracted' data), and we never change it... only when parent destroys us
            currentPreppedValueStringified: null,  // stringified version of current value (prepped/extracted), updated whenever we edit cells... before we save to form
            // initialOrderStringified: null,  // array of localIds in initial order, for change detection of order changes
            // currentOrderStringified: null,  // array of localIds in current order, updated whenever we edit cells or move rows... before we save to form
            // gridIsInControlOfData: false,  // set to true when we make ANY local changes, knowing that grid is in control of data... we set it back to false when we save to form
            allowUpdateLocalFromOutside: true,  // set to false when we are the ones updating context.model, so we don't trigger infinite loops - sets who is in control of the data... outside or grid
            hasLocalChanges: false,  // set when value/order changes, when we run change detection
            wereErrorsEverChecked: false,  // start false, set to true the first time we actually check for errors with data that is worth checking -- should only be used to control the initial check
            errorsByLocalId: {},  // map of localId to { fieldName: [errorStr, ...], ... } for that row
            saveRequestedAfterEdit: false,  // set to true when user clicks Save button in modal, so we know to save next time we do change detection
            isCellEditActive: false,  // used for UI -- use the grid API to check actual cell edit status
            // handling paste operations
            isPasteInProgress: false,  // set to true when we detect a paste event, and set it back to false after we process the paste -- used to prevent certain things from happening during paste (like auto-saving on cell edit end)
            pastedRowIds: new Set(),  // when we do a paste, we add the localIds of the pasted rows here, so we can do certain things for those rows when paste operation is complete
            // COLDEF THINGS
            liveGridColsByField: {},
            validationFields: [],  // field keys currently active in this grid instance; validation should only use these
            colDefs: [],  // for the EDIT grid
            rowDefaults: {},  // default values for new rows, built from liveGridColsByField
            // GENERAL GRID THINGS
            localGridOptions: {},  // set this in beforeMount, merge in props.gridOptions
            localGridContext: {},  // set in beforeMount, like localGridOptions
            gridApi: null,
            gridColumnApi: null,
            editGridPopupParent: null,  // set when modal opens
            addMultiRowsNumber: 10,  
            // DISPLAY GRID STUFF
            displayColDefs: [],  // colDefs actually being displayed (after applying colConfig)
            canShowDisplayGrid: false,
            displayGridApi: null,
            displayGridColumnApi: null,
            displayGridOptions: {},  // set this in beforeMount, merge in props.gridOptions
            displayGridContext: {},  // set in beforeMount, like localGridOptions
            displayGridPopupParent: null,  // set in mounted
            // MODAL THINGS
            currentValueBeforeModalEdit: null,  // stash currentValue when we first show edit grid, so we can revert to it if user cancels
            editModalShowing: false,
            canShowEditGrid: false,  // set true once we have modal mounted.. need that for popup editors parent
            showModalCancelConfirm: false,  // set to true when user tries to close modal but we have unsaved changes
            // FOR MONEY FIELDS 
            defaultCurrencyCode: null,  // if there are money fields, we have a dropdown where user can set default currency for those fields
        }
    },
    watch: {
        'context.model': {  // seems this is triggered on any change to context???
            // handler: 'setLocalValueFromOutside',
            handler: function handleModelChange(newVal) {
                const gridApiReady = this.gridApi !== null;
                console.log('in watch context.model - we will now call setLocalValueFromOutside -- gridApiReady?', gridApiReady, newVal);
                this.setLocalValueFromOutside(newVal, false); // we will reset initial, because if the watcher catches it, the 'parent' will also know about it, so this is the new starting point for diffing
            },
            immediate: true,  // only do this if not also doing it in created/mounted... but we do that to make sure we have cols setup first
            deep: true,
            // handler: function handleModelChange(newVal, oldVal) {
            //     console.log('in watch context.model - newVal:', newVal, 'oldVal:', oldVal);
            //     // we only update providedRowData if the newVal is actually different from our currentValue (to avoid infinite loops)
            //     const isDirectEqual = newVal === oldVal;
            //     const isReallyEqual = isEqual(newVal, oldVal);
            //     const isEqualToCurrent = newVal === this.currentValue;
            //     const isReallyEqualToCurrent = isEqual(newVal, this.currentValue);
            //     console.log('in watch context.model - equality checks:');
            //     console.table({
            //         isDirectEqual,
            //         isReallyEqual,
            //         isEqualToCurrent,
            //         isReallyEqualToCurrent,
            //     });
            //     if (!isEqual(newVal, this.currentValue)) {
            //         console.log('in watch context.model - newVal is different from currentValue - updating providedRowData');
            //         this.setLocalValueFromOutside(newVal || []);  // make sure its an array
            //     } else {
            //         console.log('in watch context.model - newVal is same as currentValue - not updating providedRowData');
            //     }
            // },
            // immediate: true,  // not immediate, we call it in created/mounted anyways, once all the necessary things are setup
        },
    },
    computed: {
        addItemText() {
            return this.context?.addLabel || '+ Add Item';
        },
        // rowDefaults() {
        //     // we don't add defaults if there isn't one defined, right?
        //     const defs = {};
        //     Object.entries(this.liveGridColsByField).forEach(([field, gridCol]) => {
        //         const defVal = gridCol.getDefaultValue();
        //         if (defVal !== undefined) {  // make sure getDefaultValue() returns undefined if you specifically do not want to set a default (like for things where default is set on backend)
        //             defs[field] = defVal;
        //         }
        //     });
        //     return defs;
        // },
        fieldLabel() {
            return this.context?.label || '';
        },
        isReadOnly() {
            return this.context?.attributes?.disabled || false;
        },
        canAddRows() {
            return !this.isReadOnly && !this.noAdd;
        },
        canRemoveRows() {
            return !this.isReadOnly && !this.noRemove;
        },
        canReorderRows() {
            return !this.isReadOnly && !this.noReorder;
        },
        minRows() {
            return this.context?.minimum || 0;
        },
        maxRows() {
            return this.context?.limit || null;
        },
        hasRowErrors() {
            return Object.keys(this.errorsByLocalId).length > 0;
        },
        isStringifiedValueDifferent() {
            return this.currentPreppedValueStringified !== this.lastOutsidePreppedValueStringified;
        },
        displayGridSizeParams() {
            const finalHeight = typeof this.gridHeight === 'number' ? `${this.gridHeight}px` : this.gridHeight;
            return {
                width: '100%',
                // height: '800px',
                height: finalHeight,
                domLayout: 'normal',
            };
        },
        editGridSizeParams() {
            // edit grid will expand to the height allowed by the modal, so we just set height to 100% and make sure modal is sized based on content 
            return {
                width: '100%',
                height: '100%',  // for autoHeight, we don't set this
                domLayout: 'normal',
            }
        },
        // gridSizeParams() {  // NOTE: this works, but we are now setting things differently for display and edit grids, so use those computeds instead
        //     // TODO - make separate ones for edit grid and display grid???  display grid should always be autoHeight probably, but edit grid should just be set based on screen size of the modal/viewport?  later...
        //     // a single object that holds all the things that can affect grid size (especially height)
        //     // we put them all in one object because they need to work together, centralize the logic
        //     let finalWidth = '100%';  // for now, always 100% width
        //     // for height, we'll assume domLayout is autoHeight (because this is likely always used for relatively small number of rows (<100) and we want it to grow/shrink as needed)
        //     // only change to something else ('normal') if we have that set in gridOptions prop
        //     let domLayout = this.gridOptions?.domLayout === 'normal' ? 'normal' : 'autoHeight';
        //     let finalHeight = undefined;
        //     if (domLayout === 'normal') {
        //         // if normal, we use the provided gridHeight prop (which can be number or string)
        //         finalHeight = typeof this.gridHeight === 'number' ? `${this.gridHeight}px` : this.gridHeight;
        //     } else {
        //         // if autoHeight, we do NOT set height, let it grow/shrink as needed
        //         finalHeight = undefined;
        //     }
        //     return {
        //         width: finalWidth,
        //         height: finalHeight,
        //         domLayout: domLayout,
        //     };
        // },
        // MODAL THINGS
        modalSize() {
            const colDefCount = this.colDefs.length;
            if (colDefCount <= 3) {
                return 'md';
            } else if (colDefCount <= 6) {
                return 'lg';
            } else if (colDefCount <= 8) {
                return 'xl';
            } else {
                return 'xxl';  // or 'full'?  or whatever you call it...
            }
        },
        // MONEY THINGS
        hasMoneyFields() {
            return Object.values(this.liveGridColsByField).some(col => col.colTypeKey === 'money');
        },
        dynamicCurrencyCodeOptions() {
            // go through all cols/colDefs and build superset array of all currency codes
            // the money editor components will only use a selected default if its allowed for their own col, so should be safe to make a superset of codes
            const currencyCodeSet = new Set();
            // for this one, we will go through all the colDefs and look at cellEditorParams.currencyOptions... thats closer to what the editor will actually use
            this.colDefs.forEach(colDef => {
                if (colDef.cellEditor === 'moneyCellEditor' && colDef.cellEditorParams && Array.isArray(colDef.cellEditorParams.currencyOptions)) {
                    colDef.cellEditorParams.currencyOptions.forEach(code => currencyCodeSet.add(code));
                }
            });
            return Array.from(currencyCodeSet).sort();  // return sorted array of unique currency codes
        },
        defaultCurrencyOptions() {
            // array of currency options as OBJECTS with {value, label}, with a placeholder option at start
            // the defaultCurrencyCodeOptions is built dynamically based on the fields we have
            return [{value: null, label: '---'}, ...this.dynamicCurrencyCodeOptions.map(code => ({value: code, label: code}))];
        },
    },
    created() {
        console.log(`************** FormulateAgGrid -- CREATED - ${this.fieldLabel} **************`);

        // setup our cols / colDefs / defaults for each col first... needed for lots of things
        this.setupCols();

        // register a rule with the FormulateInput that wraps this, so it checks our own internal validation
        // args are (ruleCallable, args, ruleName, message (string)... maybe some other stuff???)
        // callable will be passed the validation context and should return true/false
        const ruleCallable = () => {  // will be passed the validation context but we don't need it....maybe
            // we just check if we have any row errors
            const isLocalValid = this.getIsValid();
            console.log('in ag-grid validation ruleCallable - isLocalValid:', isLocalValid);
            return isLocalValid;
        };
        // we have to go to parent to register the rule.... HACKY!!!
        this.$parent.registerRule(ruleCallable, null, 'ag-grid-validation', 'Datagrid is not valid');  // registerRule is not a thing...
        
        // setup the initial row data -- must have cols build for this already
        // and set our initialRowsRaw 
        // this.initialRowsRaw = Array.isArray(this.context?.model) ? cloneDeep(this.context.model) : [];
        // and set providedRowData from that... but need to add the localIds and _isValid, etc. -- only after we build colDefs though
        // now in watcher with immediate: true
        // TODO - must actually make sure the validation is triggered somehow on the first load...
    },
    beforeMount() {
        console.log(`************** FormulateAgGrid -- BEFORE MOUNT - ${this.fieldLabel} **************`);
        this.setLocalGridOptions();
        this.setLocalGridContext();
    },
    mounted() {
        console.log(`************** FormulateAgGrid -- MOUNTED - ${this.fieldLabel} **************`);
        this.displayGridPopupParent = this.$refs.displayGridContainer;  // try to use our outer div as popup parent, otherwise body
        console.log('in mounted - setting displayGridPopupParent to:', this.displayGridPopupParent);
        // gotta build colDefs and liveGridColsByField first, since we need those for adding rows next
        // const gridCols = this.buildGridCols();
        // this.liveGridColsByField = Object.fromEntries(gridCols.map(col => [col.field, col]));
        // this.colDefs = [controlCol, ...gridCols.map(col => ({...col.getColDef()}))];
        // // AND must set the defaults... they are used when adding rows -- we had that as computed, but should be no need.... right?
        // const rowDefaults = {};
        // Object.entries(this.liveGridColsByField).forEach(([field, gridCol]) => {
        //     const defVal = gridCol.getDefaultValue();
        //     if (defVal !== undefined) {  // make sure getDefaultValue() returns undefined if you specifically do not want to set a default (like for things where default is set on backend)
        //         rowDefaults[field] = defVal;
        //     }
        // });
        // this.rowDefaults = rowDefaults;

        // NEW WAY OF ADDING MIN ROWS -- you might need to check the 'mode' we are in... could be edit or view (later)
        // we want to add blank rows, and those SHOULD be considered a change from the last value from outside... right?
        // therefore we do not use this.setLocalValueFromOutside... we just build the array we want, then set that on this.providedRowData
        // grid will not be loaded yet, until we get to end of this and set canShowGrid to true
        
        // NOT RUNNING THIS HERE... only when we are about to show the edit grid
        // const currentRows = Array.isArray(this.providedRowData) ? [...this.providedRowData] : [];  // likely set in created or watcher -- with our extra local stuff added
        // if (currentRows.length < this.minRows) {
        //     const toAdd = this.minRows - currentRows.length;
        //     console.log(`in mounted - currentRows.length < minRows - will add ${toAdd} rows`);
        //     for (let i = 0; i < toAdd; i++) {
        //         // this.addRowToGrid({ensureVisible: false, redrawRows: false});
        //         currentRows.push(this.createBlankLocalRow());
        //     }
        //     // now just set on this.providedRowData... we do NOT call setLocalValueFromOutside, because we want to keep the lastOutsidePreppedValueStringified as-is
        //     this.providedRowData = currentRows;
        //     // TODO - need to run change detection here, since we just added rows??? but grid is not loaded yet... do we need?
        //     this.handleDataChanges();  // this will update currentValue, currentPreppedValue, currentPreppedValueStringified, etc.
        // }

        // // now make sure we have at least minRows rows
        // // should instead just call it this.initialValueRaw (not with defaults, etc.) -- set that in created, then do something with it here, once colDefs are built
        // const initialRowsFromOutside = [];  // build from this.initialRowsRaw, and any blank rows we need to add
        // if (this.initialRowsRaw.length > 0) {
        //     // gotta set local value first with the non-local structure....
        //     this.setLocalValueFromOutside(this.initialRowsRaw, true);  // true means we reset lastOutsidePreppedValueStringified, etc.
        //     initialRowsFromOutside.push(...this.initialRowsRaw);  // copy the raw initial rows... we'll add localIds, _isValid, etc. in setLocalValueFromOutside
        // }
        // // now see if we have to add any blank rows to meet minRows
        // // TODO - we gotta save that, before we add blank rows... we need to know what the initial value is for change detection
        // // we should set it, using setLocalValueFromOutside -- THEN add blank rows if needed (and if in edit mode)
        // if (this.initialRowsRaw.length < this.minRows) {
        //     console.log('in mounted - initialRowsRaw.length < minRows - will add rows');
        //     const toAdd = this.minRows - this.initialRowsRaw.length;
        //     for (let i = 0; i < toAdd; i++) {
        //         // this.addRowToGrid({ensureVisible: false, redrawRows: false});
        //         initialRowsFromOutside.push(this.createBlankLocalRow());
        //     }
        //     // we just call setLocalValueFromOutside... which will replace the entire localValue array and handles change detection stuff
        //     // then we call 
        //     this.setLocalValueFromOutside(initialRowsFromOutside, false);  // do not automatically reset initial value... we just added blank rows locally... that's different from what parent gave us
        // }
        // done?  allow the grid to show
        this.canShowDisplayGrid = true;  // this.displayColDefs.length > 0;

        // MUST RUN INITIAL VALIDATION HERE -- using this.providedRowData or this.lastOutsideValue (should be same?  why do you have both?)
        // those would have been set in setLocalValueFromOutside, which should have been called in created or watcher with immediate: true
        if (this.providedRowData && Array.isArray(this.providedRowData)) {
            console.log('in mounted - running initial validation with providedRowData:', this.providedRowData);
            this.providedRowData.forEach(row => {
                this.runAndSetValidationSingleRowData(row);
            });
            // that just recorded the errors on EACH ROW -- now we need to set the errorsByLocalId for the whole grid
            this.setErrorsFromRowData(this.providedRowData);
            // this.handleDataChanges();  // should not need this... it only runs when we have the grid loaded... that's its purpose.  edit grid will not be loaded yet
        }
        // FINALLY, tell parent to run validation... need this because our change detection will not have fired, which is what we normally rely on to trigger validation
        this.context.performValidation();
    },
    methods: {
        getIsValid() {
            // we register this with parent as another validation rule (in mounted or created hook)
            // make sure it can be safely called at any time (with or without gridApi, etc)
            const hasNoErrors = Object.keys(this.errorsByLocalId).length === 0;
            console.log(`checking for errors in getIsValid on ag-grid field ${this.fieldLabel}: wereErrorsEverChecked=${this.wereErrorsEverChecked}, errorsByLocalId:`, this.errorsByLocalId);
            return this.wereErrorsEverChecked && hasNoErrors;  // if we never checked, then we return false to be safe
            // then we must make sure once the grid loads, we run validation (handleDataChanges) at least once to run it
        },
        setLocalGridOptions() {
            // set different ones for display/edit grids, but share as much as possible
            const sharedDefaultColDef = {
                sortable: true,
                filter: true,
                resizable: true,
                editable: false,  // set to !this.isReadOnly for the edit grid
                minWidth: 70,
                width: 70,  // I don't think we can set both width and flex... 
                // flex: 1,  // NOTE: if you manually resize a column (even via API), it will no longer flex... so we are not using this... setting width instead
                suppressMenu: true,
            };
            const sharedGridOptions = {
                // these are options that are the same for both display and edit grids... we put them here to avoid repeating them in both places
                rowSelection: 'single',
                enableRangeSelection: true,
                suppressCopyRowsToClipboard: true,  // suppresses copying the WHOLE row when copying from a cell... so we can only copy the selected cells instead (good)
                ...this.gridOptions,  // from prop
                columnTypes: {
                    // ...colDefTypes,
                    ...(this.gridOptions.columnTypes || {}),
                },
                getRowNodeId: function(data) {
                    return data ? data[localIdField] : '';  // MUST HAVE AN ID FIELD on each row data object for this to work
                },  // use this to have grid use your own defined node IDs... or let it use its own.  should return strings.
                frameworkComponents: {
                    controlCellRenderer: ControlCellRenderer,
                    booleanCellEditor: BooleanCellEditor,
                    bootstrapVueDatePickerEditor: BootstrapVueDatePickerEditor,
                    customTextAreaEditor: CustomTextAreaEditor,
                    multiSelectCellEditor: MultiSelectCellEditor,
                    moneyCellEditor: MoneyCellEditor,
                    agGridFileRenderer: AgGridFileRenderer,
                    agGridFileEditor: AgGridFileEditor,
                },
                processCellFromClipboard: this.handleProcessCellFromClipboard,
                processCellForClipboard: this.handleProcessCellForClipboard,
                processDataFromClipboard: this.handleProcessDataFromClipboard,
            };
            this.displayGridOptions = {
                // the DISPLAY grid
                ...sharedGridOptions,
                defaultColDef: {
                    ...sharedDefaultColDef,
                    editable: false,  // override for display grid
                    cellClassRules: {
                        'row-valid': function(params) {
                            return params.data && params.data._isValid === true;
                        },
                        'row-invalid': function(params) {
                            return [false, null, undefined].includes(params.data && params.data._isValid);  // change later to only show red if actually false?
                        },
                    },
                },
            };
            this.localGridOptions = {
                // the EDIT grid
                ...sharedGridOptions,
                defaultColDef: {
                    ...sharedDefaultColDef,
                    editable: !this.isReadOnly,  // override for edit grid
                    cellClassRules: {
                        // 'cell-valid': function(params) {
                        //     return params.data && params.data._isValid === true;
                        // },
                        'cell-invalid': function(params) {
                            if (params.colDef?.field && params.data && params.data._errors) {
                                const fieldErrors = params.data._errors[params.colDef.field];
                                return fieldErrors && fieldErrors.length > 0;
                            } else {
                                return false;
                            }
                        },
                        'cell-read-only': function(params) {
                            return params.colDef && params.colDef.editable === false;
                        },
                    },
                },
                // EDIT ONLY THINGS
                rowDragManaged: true,
                enableFillHandle: true,
                fillHandleDirection: 'y',
                enterMovesDownAfterEdit: true,
                animateRows: true,
            };
        },
        // clipboard handlers -- putting here to more easily share them between edit and display grids
        handleProcessCellFromClipboard(params) { // custom processing of PASTE values INTO the grid
            // params has { column, node, value, api, columnApi, context, type (the paste type... clipboard, dragCopy, export) }
            // value is the raw value to paste - return the value you want to actually paste
            // use our LiveGridCol to parse the value since the grid doesn't do it properly in all cases (depends on parser, editor, etc.)
            console.log('in handleProcessCellFromClipboard - params:', params);
            const rawValue = params.value;
            const fieldKey = params.column.colDef?.field;
            if (!fieldKey) {
                return params.value;  // no field... just return the value as-is... or should we return null???
            }
            const liveCol = this.liveGridColsByField[fieldKey];
            // TODO - check if its money field?  if we need to rely on the default currency for the grid, we will need to set that here
            // but the value coming in might have a currency symbol... if so, we can use that!
            return liveCol ? liveCol.parseValue(rawValue) : rawValue;
        },
        handleProcessCellForClipboard(params) {  // custom processing of COPY/CUT values FROM the grid
            // params has { column, node, value, api, columnApi, context, type (the copy type... clipboard, dragCopy, export) }
            // value is the raw value to copy from the grid - return the value you want to actually copy
            // use our LiveGridCol to format the value since the grid doesn't do it properly in all cases (depends on parser, editor, etc.)
            // TODO - might want to add to/from clipboard methods to LiveGridCol???  for dates, we should export to ISO string probably, but display as local date?
            console.log('in handleProcessCellForClipboard - params:', params);
            const gridValue = params.value;
            const fieldKey = params.column.colDef?.field;
            if (!fieldKey) {
                return params.value;  // no field... just return the value as-is... or should we return null???
            }
            const liveCol = this.liveGridColsByField[fieldKey];
            return liveCol ? liveCol.formatValue(gridValue) : gridValue;
        },
        handleProcessDataFromClipboard(params) {
            // this handles the entire paste operation -- params.data is a 2D array of things to paste -- we can modify the data however we want before it gets pasted in... but we don't have info about where its going, which cell was clicked on, etc. -- hmm... might need to do this some other way.... like only replacing the entire grid?
            // data is 2D array of strings (values to paste) - return the data you want to actually paste (can be modified 2D array of strings)
            // hmm... we only have { data } in params... no info about where its going, which cell was clicked on, etc...
            // might need to do this some other way.... like only replacing the entire grid?
            console.log('in handleProcessDataFromClipboard - params:', params);
            // const dataRowCount = Array.isArray(params.data) ? params.data.length : 0;  // number of rows being pasted, its a 2D array of strings
            // const currentRowCount = this.gridApi ? this.gridApi.getDisplayedRowCount() : 0;
            const currentRange = this.gridApi.getCellRanges(); // array of { startRow: RowPosition, endRow: RowPosition, columns: Column[]}  RowPosition is { rowIndex: number, rowPinned: string|undefined}
            const currentStartRowIndex = currentRange && currentRange.length > 0 ? currentRange[0].startRow.rowIndex : null;
            const currentRowCount = this.gridApi.getDisplayedRowCount();
            const availableRows = currentStartRowIndex !== null ? currentRowCount - currentStartRowIndex : 0;
            const dataRowCount = Array.isArray(params.data) ? params.data.length : 0;  // number of rows being pasted, its a 2D array of strings
            const addRowCount = Math.max(0, dataRowCount - availableRows);
            console.log(`Handling a paste of data into the grid -- user is pasting ${dataRowCount} rows - we will add ${addRowCount} rows to the grid`);
            // check what our active row is based on selection (single select or multiple?) then we can decide if we need to add rows to accommodate the paste, or if we just overwrite existing rows...
            if (addRowCount > 0) {
                this.addRowsToGrid({count: addRowCount, ensureVisible: false, redrawRows: true});  // redraw???
            }
            return params.data;  // for now, just return the data as-is, or null to cancel the paste
        },
        setLocalGridContext() {
            this.localGridContext = {
                componentParent: this,
                ...this.gridContext,
            };
            this.displayGridContext = {
                componentParent: this,  /// hmm.... safe to do???
                ...this.gridContext,
            };
        },
        getControlCol(extraCellRendererParams) {
            const cellRendererParams = extraCellRendererParams || {};
            return {
                headerName: '',
                colId: controlColId,
                width: 65,  // make narrower if no edits allowed?
                pinned: 'left',
                cellRenderer: 'controlCellRenderer',
                cellRendererParams: {...cellRendererParams},  // for future use....
                rowDrag: this.canReorderRows,
                suppressMenu: true,
                sortable: false,
                filter: false,
                editable: false,
                resizable: false,
                suppressSizeToFit: true,
                cellClass: ['control-col'],
                cellClassRules: {
                    'row-valid': function(params) {
                        return params.data && params.data._isValid === true;
                    },
                    'row-invalid': function(params) {
                        return [false, null, undefined].includes(params.data && params.data._isValid);  // change later to only show red if actually false
                    },
                },
                // add renderer to show button for removing!
            };
        },
        setupCols() {
            // do all the things to build colDefs, liveGridColsByField, defaults, etc.
            // we only need to call this once, in created/mounted -- and it only depends on a single prop (cols)
            const gridCols = this.cols.map(cd => new LiveGridCol(cd));
            // we generate overrides to pass to each LiveGridCol based on colConfig prop -- because its often easier to manage things at the "grid" level instead of each col because cols are all introspected
            const pinnedLeftCols = Array.isArray(this.colConfig?.pinnedLeft) ? this.colConfig.pinnedLeft : [];
            const pinnedRightCols = Array.isArray(this.colConfig?.pinnedRight) ? this.colConfig.pinnedRight : [];
            const enabledFields = Array.isArray(this.colConfig?.enabled) ? this.colConfig.enabled : [];  // only honor this if its non-empty
            const useEnabledFields = enabledFields.length > 0;
            const orderedEnabledFields = useEnabledFields ? [...enabledFields] : gridCols.map(col => col.field);  // use enabledFields order if we have it, otherwise just the order of gridCols -- grid will handle pinned stuff on its own
            console.log('in setupCols - colConfig:', this.colConfig, 'pinnedLeftCols:', pinnedLeftCols, 'pinnedRightCols:', pinnedRightCols, 'orderedEnabledFields:', orderedEnabledFields);
            // TODO - use them....

            this.liveGridColsByField = Object.fromEntries(gridCols.map(col => [col.field, col]));  // for ALL possible cols, even if not enabled... we pull out from here
            const validationFields = [];
            const colDefs = [];  // we build this up below... then set on this.colDefs at the end
            const displayColDefs = [];  // for the DISPLAY ONLY grid, which might have different cols (no control col) or different params on each col
            const rowDefaults = {};  // build this up as we go too, ONLY with the enabled fields, or else we might set defaults for fields that are not even in the grid
            // build the cols, in order, applying any overrides from colConfig -- then do the control col at the end
            orderedEnabledFields.forEach(fld => {
                const col = this.liveGridColsByField[fld];
                console.log(`in setupCols - processing field: ${fld}, col:`, col);
                if (!col) {
                    console.error(`in setupCols - field in colConfig.enabled not found in cols: ${fld} -- we will disregard it`);
                    return;
                }
                // BUILD COL DEF
                const overrides = {};
                // NOTE: each col will have read_only=True from backend if the form is read-only already.... so we may not need to add here also
                // if you set it here, it isn't reactive... but maybe that is OK
                if (this.isReadOnly) {
                    overrides['editable'] = false;
                }
                if (pinnedRightCols.includes(fld)) {  // right takes precedence if in both, because right is less common, so if here, we likely specifically want it
                    overrides.pinned = 'right';
                } else if (pinnedLeftCols.includes(fld)) {
                    overrides.pinned = 'left';
                }
                const colDef = {...col.getColDef(overrides)};  // get the base colDef
                const isEditable = !!colDef.editable;  // AFTER building the colDef, check if it is editable.  If not, we will IGNORE it for validation
                const isCalculatedField = !!col.colSchema.is_calculated;  // this is added by backend -- if we have a calculated field, then editable will already be false
                // we might have fields that are not editable, but still not calculated fields
                // for now, we DROP all calculated fields from the EDIT grid, but not display grid
                // but we DO show read-only fields that are not calculated -- because they might have useful info (like a label field for the row, etc.) -- we just won't validate them since they are not editable
                if (isEditable) {
                    validationFields.push(fld);  // add it to the list of fields we use to validate, only if editable
                } else {
                    console.log(`DROPPING COL FROM EDIT GRID validationFields (not editable): ${fld}`);
                }
                if (!isCalculatedField) {
                    // we only add to colDefs (for edit grid) if NOT a calculated field
                    // put pinned left cols first?  no... grid will take care of that
                    // order matters for things like Excel exports, but backend should just put things in the proper order... that's the right place to manage it
                    // otherwise it is too messy here to manage the order in edit grid, display grid, excel export, etc.
                    colDefs.push(colDef);  // must still push all... we have some read-only fields that we WANT to show in edit grid (like labels of a row) but not necessarily all things
                }
                // now build displayColDef... which is similar, but not exactly the same -- no control col, and not editable, etc.
                const displayColDef = {...cloneDeep(colDef), editable: false};  // make sure not editable -- any other things?
                displayColDefs.push(displayColDef);
                // GET DEFAULT VALUE (if any)
                const defVal = col.getDefaultValue();
                if (defVal !== undefined) {  // make sure getDefaultValue() returns undefined if you specifically do not want to set a default (like for things where default is set on backend)
                    rowDefaults[fld] = defVal;
                }
            });
            // currently only adding the control col if it is editable, otherwise no need for it based on what it does now
            // also will not include it if we don't allow reorder AND do not allow remove
            const shouldHaveControlCol = this.canReorderRows || this.canRemoveRows;  // if we can either reorder or remove, then we need the control col... if neither, then we don't need it
            if (!this.isReadOnly && shouldHaveControlCol) {
                // TODO - add more logic here for noAdd/noRemove, etc. noDrag?
                // currently you only do this if not isReadOnly.... but maybe stop doing that if control col can be smart enough
                // const controlCol = getControlCol({noAdd: this.isReadOnly, noRemove: this.isReadOnly});  // we were going to pass these in, but instead we have the component just check the parent component for its state
                const controlCol = this.getControlCol();
                colDefs.unshift(controlCol);
                // do not add control col to displayColDefs
            }

            // gridCols.forEach(col => {
            //     if (useEnabledFields && !enabledFields.includes(col.field)) {
            //         return;  // skip this col, not in enabled list
            //     }
            //     const overrides = {};
            //     if (pinnedRightCols.includes(col.field)) {  // right takes precedence if in both, because right is less common, so if here, we likely specifically want it
            //         overrides.pinned = 'right';
            //     } else if (pinnedLeftCols.includes(col.field)) {
            //         overrides.pinned = 'left';
            //     }
            //     const colDef = {...col.getColDef(overrides)};  // get the base colDef
            //     this.colDefs.push(colDef);
            // });
            // NOW SET ALL THE THINGS YOU BUILT UP ABOVE ON THE INSTANCE
            this.validationFields = validationFields;
            console.log('in setupCols - validationFields:', this.validationFields);
            this.colDefs = colDefs;  // set it on the instance when all are done
            this.displayColDefs = displayColDefs;
            // this.colDefs = [controlCol, ...gridCols.map(col => ({...col.getColDef()}))];
            // AND must set the defaults... they are used when adding rows -- we had that as computed, but should be no need.... right?
            // Object.entries(this.liveGridColsByField).forEach(([field, gridCol]) => {
            //     const defVal = gridCol.getDefaultValue();
            //     if (defVal !== undefined) {  // make sure getDefaultValue() returns undefined if you specifically do not want to set a default (like for things where default is set on backend)
            //         rowDefaults[field] = defVal;
            //     }
            // });
            this.rowDefaults = rowDefaults;
        },
        setLocalValueFromOutside(value, resetLastOutsideValue = false) {
            // NOTE: value should be our 'external' value, NOT the local data with extra stuff added
            // console.log('starting setLocalValueFromOutside - loopCounter:', this.loopCounter);
            // this.loopCounter++;
            // if (this.loopCounter > 20) {
            //     console.log('loopCounter exceeded 20, aborting to avoid infinite loop');
            //     return;
            // }
            // console.log(`STARTING setLocalValueFromOutside for grid ${this.fieldLabel} - value:`, value, 'resetLastOutsideValue:', resetLastOutsideValue);

            // somme flags just for controlling what happens in here...
            let willUpdateLocalGridData = false;  // set to true if we actually update the local grid data
            const isLastOutsideValueNotSet = this.lastOutsidePreppedValueStringified === null; // if it is specifically null, that means we never set it, or specifically reset it
            let willUpdateLastOutsideValue = resetLastOutsideValue || isLastOutsideValueNotSet || false;  // set this based on the flag, or based on whether its currently null
            const isChangedFromOutside = !isEqual(value, this.lastDataFromOutsideRaw);  // TODO - is this the right thing to compare?  or should we normalize them differently first?
            this.isChangedFromOutside = isChangedFromOutside;  // set this so we can see it in the Vue devtools
            // handle setting the 'local' version of the new data we got from outside
            
            // before we change anything, check if we have any local changes... if we do, and the new value is different from last time, that means parent changed something while we had local changes... that is probably a problem
            // TODO - do we need allowUpdateLocalFromOutside and/or gridIsInControlOfData here???  ideally not....

            if (isChangedFromOutside) {
                if (this.hasLocalChanges && !this.allowUpdateLocalFromOutside) {
                    const isGridReady = this.gridApi !== null;  // maybe ignore local changes if grid is not ready yet???
                    console.error('in setLocalValueFromOutside - we have local changes AND new value from outside is different from last time and allowUpdateLocalFromOutside is FALSE - potential conflict! - isGridReady?', isGridReady);
                    // TODO - now what.....?  abort?
                } else {
                    // if here, we have NEW data from outside, and we do NOT have local changes... so we can proceed with updating local data AND resetting initial value stuff
                    willUpdateLastOutsideValue = true;  // if we have new data from outside, we reset the initial value stuff
                    willUpdateLocalGridData = true;  // we will update the local grid data since we have new data from outside, and no local changes
                }
                // we do not care about the case where we have local changes but the new value from outside is same as last time... we just ignore it, all the local change management stuff should handle it
            }
            console.log('in setLocalValueFromOutside - isChangedFromOutside:', isChangedFromOutside, 'willUpdateLastOutsideValue:', willUpdateLastOutsideValue, 'willUpdateLocalGridData:', willUpdateLocalGridData);

            const anyChangesNeeded = willUpdateLastOutsideValue || willUpdateLocalGridData;
            const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
            
            // TODO - remove this ternary below??  seems dangerous
            const newLocalRowData = anyChangesNeeded ? valueArray.map(r => this.createLocalRow(r)) : [];   // this ternary is ONLY so we skip the createLocalRow stuff if we are not actually changing anything... faster
            // NOTE: you might already have the extra stuff (local stuff) added to each row?  so make sure its safe to call multiple times on a row

            if (anyChangesNeeded) {
                // we always update this (protected by our 'anyChangesNeeded' flag), since it is what is shown in the display grid
                // keep it out of the mess below, should be able to be independent from that... and we cloneDeep to be safe so we don't mess things up between the grids
                this.lastOutsideValue = cloneDeep(newLocalRowData);
            }

            // set lastOutsidePreppedValue/Stringified first... then set providedRowData... then call handleDataChanges to update currentValue, currentPreppedValue, etc... but setting the new row data might trigger that anyways, which is why we should set the initial stuff first
            if (willUpdateLastOutsideValue) {
                // TODO -- should we diff the previous lastOutsidePreppedValue with the new one, basically abort everything else if they are same?  
                this.lastDataFromOutsideRaw = cloneDeep(value);  // make sure we clone it so we can do proper comparisons later... this does not happen frequently
                this.lastOutsidePreppedValue = this.prepRowDataForBackend(newLocalRowData);
                this.lastOutsidePreppedValueStringified = JSON.stringify(this.lastOutsidePreppedValue);
            }

            if (willUpdateLocalGridData) {
                // must set the new lastOutsidePreppedValue first, if we are going to set the local grid data, so that the change detection stuff works with the new "baseline" data
                // only proceed with setting providedRowData if we have a real change - otherwise we'll have excessive updates to the grid
                // but we must compare with 'extracted' data otherwise we might have changes just to _localId etc...
                // and we can't compare to the incoming raw data, because that might just have different defaults, etc.... so we have to compare the raw -> local -> prepped/extracted data 
                // TODO - add a shortcut before using isEqual that checks for gross differences... will be much more efficient and will catch most things
                const newPreppedValue = this.prepRowDataForBackend(newLocalRowData);
                const isLocalDataDifferent = !isEqual(newPreppedValue, this.currentPreppedValue);
                if (!isLocalDataDifferent) {
                    // console.log('in setLocalValueFromOutside - new local row data is SAME as existing currentPreppedValue - not changing local grid data');
                    // if the prepped/extracted data is same as currentPreppedValue, that means nothing really changed, so we do NOT update providedRowData or call handleDataChanges
                    willUpdateLocalGridData = false;  // reset this flag since we are not actually changing anything
                } else {
                    console.log(`in setLocalValueFromOutside on ${this.fieldLabel} - new local data is DIFFERENT than currentPreppedValue - will change local grid data`);
                    const theDiffs = detailedDiff(newPreppedValue, this.currentPreppedValue);
                    console.log(`in setLocalValueFromOutside on ${this.fieldLabel} - detailedDiff between new local row data and currentPreppedValue:`, theDiffs);
                    if (this.gridApi) {
                        // if grid is ready, we can use setRowData which is more efficient, and maybe needed for deep change detection?
                        // TODO - what about adding empty rows here?  might need to use transactions instead???  should we even allow that?  since grid has already loaded???? TBD....
                        this.gridApi.setRowData(newLocalRowData);  // this will reset the entire grid data... might be slow for large datasets... but simpler
                        // we will then let the handleDataChanges call below handle updating things using the row data we just set -- no need to call it here
                    } else {
                        // if grid is not ready yet, we just set providedRowData, and when grid loads it will use that
                        // what about setting min rows?  TBD....  grid SHOULD do that when it loads, if needed....
                        console.log(`in setLocalValueFromOutside on ${this.fieldLabel} - gridApi not ready yet, just setting providedRowData - grid will load with this data`);
                        this.providedRowData = newLocalRowData;  // update the grid data -- we don't use transactions since we're just replacing the entire thing
                        // TODO TODO TODO - call runAndSetValidationSingleRowData on each row here so we have errors set before grid is ready and we can have parent get our actual validation status
                        // NOTE: this actually gets called on initial load, even before we run setupCols -- and we need that to run validation
                        // so... we will need to run validation on this.providedRowData or this.lastOutsideValue? in beforeMount or mounted once we have cols setup

                        // if grid is not ready yet, our normal checking for errors will not have run... we need to trigger that here
                        // either check errors only (using rowData structure) or just call handleDataChanges which will check everything???
                        // just checking errors seems "safer" so we do that for now
                        this.handleDataChanges(newLocalRowData); // do it below...?
                    }
                    // should we set this inside the if (willUpdateLocalGridData) block?  or always set it if we are changing the lastOutsidePreppedValue?
                    // this.allowUpdateLocalFromOutside = false;  // turn it back off all the time... only AFTER we possibly set it - we only turn it on when we are the ones updating context.model
                }
            }

            // when it is all done, call handleDataChanges to update currentValue, currentPreppedValue, etc. -- but ONLY if certain things changed
            if (willUpdateLastOutsideValue || willUpdateLocalGridData) {
                // TODO - check if grid is loaded, and if not, then pass in this.providedRowData????  will happen on initial load - we at least need to check for errors!
                // actually.... we can do that (check for errors) above in the if/else (else) if grid is not ready yet -- so we just call handleDataChanges here and let it short-circuit if grid is not ready
                // if (this.gridApi) {
                //     this.handleDataChanges();  // if grid is ready, we can just call this and it will get the current data from the grid
                // } else if (willUpdateLocalGridData) {
                //     // if grid is not ready yet, we pass in the new local row data we just set, since handleDataChanges won't be able to get it from the grid yet
                //     this.handleDataChanges(newLocalRowData);
                // }
                this.handleDataChanges();  // this will update currentValue, currentPreppedValue, currentPreppedValueStringified, etc.
            }
            // this.handleDataChanges();  // we always call this... sometimes things got missed... not sure why....?


            // const isDifferent = true; // just always reset things... as long as we ONLY call this for changes from parent!   !isEqual(value, oldValue);  // using isEqual might still miss things! // value !== oldValue;  // TODO - might need to stringify them.... otherwise it checks by reference?
            // console.log('in setLocalValueFromOutside - comparing new value to currentPreppedValue:', this.currentPreppedValue);
            // const isDifferent = !isEqual(value, this.currentPreppedValue); // using isEqual might still miss things! // value !== oldValue;  // TODO - might need to stringify them.... otherwise it checks by reference?
            // if (!isDifferent) {
            //     console.log('in setLocalValueFromOutside - value is SAME as currentPreppedValue - not changing local grid data');
            //     // ONLY proceed with setLocalSelection if we have a real change - otherwise we'll have excessive updates
            //     return;
            // }
            // if (this.gridIsInControlOfData) {
            //     console.log('in setLocalValueFromOutside - gridIsInControlOfData is TRUE - so we will NOT update the local grid data');
            //     return;  // if grid is in control of data, we do NOT update from outside... this avoids infinite loops - but 
            //     // NOTE: if you have data changes locally, and parent has data changes, that can be a problem... you should be able to see that, right?  diff previous initial value with new one?
            // }
            // if (!this.allowUpdateLocalFromOutside) {
            //     console.log('in setLocalValueFromOutside - allowUpdateLocalFromOutside is FALSE - aborting to avoid infinite loop');
            //     this.allowUpdateLocalFromOutside = true;  // reset it for next time... except I think it gets called twice per change???
            //     return;
            // }
            // // if proceeding, that means value IS different... so we update our local grid data and call handleDataChanges to update currentValue, currentPreppedValue, etc.
            // console.log('in setLocalValueFromOutside - value is DIFFERENT than currentPreppedValue - will change local grid data');
            // const theDiffs = detailedDiff(value, this.currentPreppedValue);
            // console.log('in setLocalValueFromOutside - detailedDiff between new value and currentPreppedValue:', theDiffs);

            // // TODO - only update this when we have "real" changes?  ... not just anytime parent does something with context
            // this.providedRowData = newLocalRowData;  // update the grid data (only if different from currentPreppedValue)
            // this.handleDataChanges();
        },
        getCurrentGridData() {
            // loop through all rows in the grid and return the current row data as-is... no prep/extraction here... we do that separately
            const val = [];
            if (!this.gridApi) {
                console.error(`in getCurrentGridData on ${this.fieldLabel} - no gridApi yet, cannot get data`);
            } else {
                this.gridApi.forEachNode((node) => {  // forEachNode ignores any filters or sorts, gives all rows in current order
                    if (node.data) {
                        val.push(node.data);
                    }
                });
            }
            console.log(`in getCurrentGridData on ${this.fieldLabel} - returning current grid data:`, val);
            return val;
        },
        prepRowDataForBackend(rows) {
            // pass in the raw rowData (from localValue if no grid yet, or from result of getCurrentGridData) 
            // this is the 'extracted' data, and in the backend format we want to save to form -- our "output" format (not used internally in the grid)
            const preppedRows = [];
            const skipKeys = [localIdField, '_isValid', '_errors'];
            rows.forEach(row => {
                const preppedRow = {};  // we build up a copy of the row with only the fields we want to keep
                Object.keys(row).forEach(key => {
                    if (!skipKeys.includes(key)) {
                        // for keys that will remain (not skipped), we might need to do some conversion... for example, if its a Date object, convert to ISO string
                        const liveCol = this.liveGridColsByField[key];
                        const rawVal = cloneDeep(row[key]);
                        if (liveCol) {
                            // convert value if needed
                            preppedRow[key] = liveCol.prepValueForBackend(rawVal);
                        } else {
                            // no liveCol for this field... just copy it as-is
                            preppedRow[key] = rawVal;
                        }
                    }
                });
                preppedRows.push(preppedRow);
            });
            return preppedRows;
        },
        setErrorsFromRowData(rowData) {
            // private method to gather errors (but does not actually run validation! grid change detection does that, or you need to call it manually)
            // value should be structured as our local row data with the extra stuff (localId, _isValid, _errors) since we need to set the errors on each row for the error tracking to work
            // should either be this.currentValue, or if the edit grid is not ready yet, then the prepared local row data that we WOULD pass to the grid
            let errorsByLocalId = {};
            rowData.forEach(r => {
                // only add it if there are actual errors.... empty objects mean no errors
                if (Object.keys(r._errors).length > 0) {
                    console.log(`Row with ${localIdField}=${r[localIdField]} has errors:`, r._errors);
                    errorsByLocalId[r[localIdField]] = r._errors;
                }
            });
            this.errorsByLocalId = errorsByLocalId;  // we set this on our instance for tracking, but also return it just in case
            this.wereErrorsEverChecked = true;  // but we also must make sure we ran actual validation on each cell in each row!  in runAndSetValidationSingleRowData!
        },
        handleDataChanges(newCurrentValue) {
            // OPTIONALLY pass in a new currentValue to use -- used if we are closing the edit modal, for example... otherwise we get currentValue from the grid
            // just do the things needed to detect local changes -- basically just save currentPreppedValueStringified
            // validation should have already been run on all rows before this (make sure of that)
            console.log(`STARTING handleDataChanges in ${this.fieldLabel}`);
            if (!this.gridApi && !newCurrentValue) {  // TODO - don't abort here if we provide currentValue passed in
                console.log(`in handleDataChanges on ${this.fieldLabel} - no gridApi yet, and no new currentValue provided to handleDataChanges, so we are aborting... no changes to compare yet`);
                return;  // abort if no gridApi -- anything else we should do first???  set this.currentValue to same as providedRowData???
            }
            // turn off allowing updates from outside... we are the ones changing things now
            this.allowUpdateLocalFromOutside = false;
            this.currentValue = newCurrentValue || this.getCurrentGridData();  // must be NON-extracted data, since we need _isValid and _errors for error tracking
            // console.log('in handleDataChanges - just set this.currentValue:', this.currentValue);
            this.currentPreppedValue = this.prepRowDataForBackend(this.currentValue);
            this.currentPreppedValueStringified = JSON.stringify(this.currentPreppedValue);
            // this.currentOrderStringified = this.getCurrentRowOrderStringified();
            // this.hasLocalChanges = this.currentPreppedValueStringified !== this.lastOutsidePreppedValueStringified;  // || (this.currentOrderStringified !== this.initialOrderStringified);
            this.hasLocalChanges = !isEqual(this.currentPreppedValue, this.lastOutsidePreppedValue);
            if (this.hasLocalChanges) {
                console.log(`in handleDataChanges on ${this.fieldLabel} - hasLocalChanges is ${this.hasLocalChanges}`);
            } else {
                console.log(`in handleDataChanges on ${this.fieldLabel} - hasLocalChanges is FALSE`);
            }
            // let errorsByLocalId = {};
            // this.currentValue.forEach(r => {
            //     // only add it if there are actual errors.... empty objects mean no errors
            //     if (Object.keys(r._errors).length > 0) {
            //         console.log(`Row with ${localIdField}=${r[localIdField]} has errors:`, r._errors);
            //         errorsByLocalId[r[localIdField]] = r._errors;
            //     }
            // });
            // this.errorsByLocalId = this._gatherErrorRows(this.currentValue);  // this also just returns the errorsByLocalId object, which we set on our instance for tracking
            this.setErrorsFromRowData(this.currentValue);  // this also just returns the errorsByLocalId object, which we set on our instance for tracking
            const errorRowCount = Object.keys(this.errorsByLocalId).length;
            console.log(`from handleDataChanges on ${this.fieldLabel} - hasLocalChanges: ${this.hasLocalChanges} -- errorRowCount: ${errorRowCount}`);
            // we only need to tell parent to re-check validation if something changed, right?  otherwise we get stuck in infinite loop, because our watcher is called on any change to context...
            if  (this.hasLocalChanges) {
                console.log(`in handleDataChanges on ${this.fieldLabel} - changes detected, will tell parent to re-check validation`);
                this.context.performValidation();  // triggers parent to re-check our validation rule
            } else {
                console.log(`in handleDataChanges on ${this.fieldLabel} - no changes, will not tell parent to re-check validation`);
            }
            if (!this.gridApi) {
                // gotta set this.allowUpdateLocalFromOutside = true if no grid, because the normal way of doing that won't work
                this.allowUpdateLocalFromOutside = true;  // if no grid, we have to turn this back on here, since we won't be able to use the normal way of doing it in handleCellEditingStopped
            }
        },
        hasActiveEditCells() {
            // helper for multiple places below
            const editingCellPositions = this.gridApi?.getEditingCells();
            return editingCellPositions && editingCellPositions.length > 0;
        },
        startSaveToForm() {
            console.log('in startSaveToForm - starting save to form process');
            // call this when we want to save currentValue back to context.model
            // we have to make sure all grid edits are finished, since that's what triggers all validation stuff
            // then if still valid, then we can call saveValueToForm
            // we go directly to the API, no our flag to SHOULD proxy it, just to be safe
            if (this.hasActiveEditCells()) {
                console.log('in startSaveToForm - still have editing cells, will stop editing first, then call after all updates/validation is complete');
                this.saveRequestedAfterEdit = true;
                this.gridApi.stopEditing();  // this stops editing, which IF THERE IS CHANGED DATA will trigger our validation stuff BEFORE calling onCellEditingStopped (that's the order we want)
                return;  // return early... our handleCellEditingStopped will call us again after edits complete (that fires AFTER all our validation stuff is done)
            }
            if (this.hasRowErrors) {
                console.warn('in startSaveToForm - cannot save to form, we have row errors:', this.errorsByLocalId);
                this.$toast.warning('Please fix the errors in the grid before saving.');
                return;
            }
            console.log('in startSaveToForm - we made it through our checks, will now call the real save method');
            this.performSaveValueToForm();
        },
        performSaveValueToForm() {
            // call this when we are "done" with edits, to save currentValue back to context.model
            // we also make sure to update the order based on current grid order
            if (!this.gridApi) {
                console.warn('in performSaveValueToForm - no gridApi yet, cannot save');
                return;
            }
            if (this.hasActiveEditCells()) {
                console.warn('in performSaveValueToForm - still have editing cells, cannot save yet');
                return;  // cannot save yet, still editing
            }
            // this.gridApi.stopEditing();  // make sure to stop editing first so any in-progress edits are saved
            const currentValue = this.getCurrentGridData();  // should we just use this.currentValue???  we should have recently set that... MAYBE NEEDED FOR HAVING MULTIPLE GRIDS?
            const preppedCurrentValue = this.prepRowDataForBackend(currentValue);
            console.log(`in performSaveValueToForm - will save preppedCurrentValue to form data:`, preppedCurrentValue);
            // NOTE: we used to trigger parent to call validation here... but makes sense to change until after set set on context.model
            // we will also reset our change detection state -- SHOULD BE NO NEED -- we improved things in setLocalValueFromOutside
            // this.lastOutsidePreppedValue = null;
            // this.lastOutsidePreppedValueStringified = null;  // so that setLocalValueFromOutside will set it again
            this.allowUpdateLocalFromOutside = true;  // we want the local data to update based on whatever we set on context.model
            // KILL EDIT GRID -- this will kill gridApi, so we can't get anything from grid after this (which is fine) and we want it gone so our handleDataChanges calls abort properly
            this.killEditGrid();
            // what to do with currentPreppedValueStringified???  keep it same, right?  then we can compare what parent passes back in to whats in the grid now
            this.context.model = preppedCurrentValue;
            this.context.blurHandler();  // call this to let Formulate know we changed the value
            // TODO SHOULD WE MOVE VALIDATION CALL HERE???  just did... had it above earlier...
            this.context.performValidation();  // call this to make parent re-check our validation rule - maybe no need if we do it on every cell edit???

            if (this.editModalShowing) {
                // close the modal if open... but maybe have to wait until things settle?  nextTick???
                // some of the formulate stuff might be async?  
                this.$nextTick(() => {
                    this.editModalShowing = false;  // close the modal if open
                });
            }
        },
        addMinRowsIfNeeded() {
            const currentRows = Array.isArray(this.providedRowData) ? [...this.providedRowData] : [];  // likely set in created or watcher -- with our extra local stuff added
            if (currentRows.length < this.minRows) {
                const toAdd = this.minRows - currentRows.length;
                console.log(`in addMinRowsIfNeeded - currentRows.length < minRows - will add ${toAdd} rows`);
                for (let i = 0; i < toAdd; i++) {
                    // this.addRowToGrid({ensureVisible: false, redrawRows: false});
                    currentRows.push(this.createBlankLocalRow());
                }
                // now just set on this.providedRowData... we do NOT call setLocalValueFromOutside, because we want to keep the lastOutsidePreppedValueStringified as-is
                this.providedRowData = currentRows;
                this.handleDataChanges();  // this will update currentValue, currentPreppedValue, currentPreppedValueStringified, etc.
            }
        },
        createBlankLocalRow() {
            // create and return a single blank row data object, with localId, validation stuff, and defaults filled in
            // use this to get new 'filler' rows before grid is loaded, or when making a new row to add in a transaction to the existing grid
            // const newRowDataRaw = { ...this.rowDefaults };  // we moved this to createLocalRow -- to always add defaults for missing cols/fields
            // const newRow = this.createLocalRow(newRowDataRaw);  // this also sets validation on it
            // return newRow;
            return this.createLocalRow();  // no args means all defaults
        },
        addRowToGrid({ensureVisible = true, redrawRows = true} = {}) {
            if (this.noAdd) {
                return;
            }
            // add a SINGLE row to the grid... wrapper around addRowsToGrid
            this.addRowsToGrid({count: 1, ensureVisible, redrawRows});
        },
        addRowsToGrid({count = 1, ensureVisible = true, redrawRows = true} = {}) {
            if (this.noAdd) {
                return;
            }
            // util for adding MULTIPLE rows to grid in one transaction -- should only be used to add to the grid when its already showing
            // use other ways to do it to add rows before grid is loaded (like just adding to providedRowData) -- we don't need transactions for that, and it changes our change-detection stuff
            // TODO - only use transactions if grid is already loaded with data???  otherwise just set providedRowData?  or do that outside of here?  yes, outside of here for now
            if (!this.gridApi) {
                console.warn('in addRowToGrid - no gridApi yet, cannot add row');
                return;
            }
            const newRows = [];
            for (let i = 0; i < count; i++) {
                const newRowData = this.createBlankLocalRow();
                newRows.push(newRowData);
            }
            const transactionResult = this.gridApi.applyTransaction({ add: newRows });
            const { add } = transactionResult;
            // const firstNewNode = add && add.length > 0 ? add[0] : null;
            const lastNewNode = add && add.length > 0 ? add[add.length - 1] : null;
            this.handleDataChanges();
            if (lastNewNode && redrawRows) {
                this.$nextTick(() => {
                    this.redrawRows();
                });
            }
            if (ensureVisible && lastNewNode && this.gridApi) {
                this.$nextTick(() => {
                    this.gridApi.ensureNodeVisible(lastNewNode);  // can pass the data object instead of node, either is allowed
                });
            }
        },
        handleAddMultiRows() {
            // console.log('in handleAddMultiRows - evt:', evt, 'addMultiRowsNumber:', this.addMultiRowsNumber);
            const rowCount = this.addMultiRowsNumber;
            if (!rowCount || rowCount < 1) {
                return;
            }
            // Close the dropdown and (by passing true) return focus to the toggle button
            this.$refs.addMultiRowsDropdown?.hide(true)
            this.addRowsToGrid({count: rowCount, ensureVisible: true, redrawRows: true});  // this will handle case of noAdd... do not need to add again here
        },
        createLocalRow(rowData) {
            // TODO (maybe) - for change detection to work well, we might need to "fill" any missing fields with some empty/default value
            // that might create problems on backend?  well, we should be able to handle that... otherwise the default values aren't correct
            // we should do this for both existing rows from backend, and new empty rows we make
            const providedData = rowData ? cloneDeep(rowData) : {};
            const defaultData = cloneDeep(this.rowDefaults);  // just in case we had any objects/arrays in defaults
            const newData = { [localIdField]: nanoid(), _isValid: null, _errors: {}, ...defaultData, ...providedData };
            this.runAndSetValidationSingleRowData(newData);
            return newData;
        },
        runValidationSingleRowData(rowData) {
            // get the validation status for a SINGLE given rowData object, but DO NOT SET it on the rowData  (separate method for that -- but maybe can combine again...)
            // returns { isValid: bool, errorsByField: { fieldName: [errorStr, ...], ... } }
            const errorsByField = {};
            // Object.entries(this.liveGridColsByField).forEach(([field, col]) => {
            this.validationFields.forEach(field => {
                const col = this.liveGridColsByField[field];
                if (!col) {
                    console.error(`in runValidationSingleRowData - field in validationFields not found in liveGridColsByField: ${field} -- this should not happen, since we build validationFields from liveGridColsByField -- IGNORING FOR NOW`);
                    return;
                }
                const value = rowData[field];
                const errors = col.getErrors(value);
                if (errors.length > 0) {
                    errorsByField[field] = errors;
                }
                // console.log(`Validating row with ${localIdField} - field: ${field}, value:`, value, 'errors:', errors);
            });
            const isValid = Object.keys(errorsByField).length === 0;
            return { isValid, errorsByField };
        },
        runAndSetValidationSingleRowData(rowData) {
            // Validate the rowData object AND set _isValid, _errors on it
            const { isValid, errorsByField } = this.runValidationSingleRowData(rowData);
            // console.log(`Row with ${localIdField}=${rowData[localIdField]} is valid?`, isValid, 'errorsByField:', errorsByField);
            // are these reactive since they are inside the grid's row data???
            this.$set(rowData, '_isValid', isValid);
            this.$set(rowData, '_errors', errorsByField);
        },
        setValidationRowNode(rowNode, refreshCells = true) {
            // Validate the rowNode data, set _isValid, _errors on it, AND optionally refresh the cells to update their classes
            // use this when validating things for things already in the grid, like on cell edit
            // console.log('Validating rowNode:', rowNode);
            const data = rowNode.data;
            this.runAndSetValidationSingleRowData(data);
            if (refreshCells) {
                // optional because you might want to hold off and do this later if doing a lot of validations at once?
                this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });  // force refresh to update cellClassRules, suppressFlash?
            }
        },
        deleteRow(rowLocalId) {
            if (this.noRemove) {
                return;
            }
            console.log(`in deleteRow - will delete row with ${localIdField}=${rowLocalId}`);
            // we use grid transactions so grid knows what is going on.  but then localValue is out of sync...
            // you could solve this by setting immutableData=true on the gridOptions, but then we likely have to handle all edits ourselves....
            // const removeRows = this.localValue.filter(r => r[localIdField] === rowLocalId);
            const removeRows = [{[localIdField]: rowLocalId}];  // we can just pass in an object with the canonical row ID field... grid will find the right row to remove
            this.gridApi.applyTransaction({ remove: removeRows });
            // our control cell renderer calls this on the parent component when delete button is clicked
            // not saving to form here.... only when user clicks "Save Edits" button
            this.handleDataChanges();
        },

        // GENERIC GRID HELPERS
        redrawRows() {
            if (this.gridApi) {
                this.gridApi.redrawRows();
            }
        },
        refreshCells(params = {}) {
            if (this.gridApi) {
                this.gridApi.refreshCells(params);
            }
        },
        sizeColumnsToFit() {
            // NOTE: this is for the EDIT GRID
            // fits them to the width of the grid... but can make them really wide if you have few columns and a wide grid and really narrow if many cols
            if (this.gridApi) {
                this.gridApi.sizeColumnsToFit();
            }
        },
        autoSizeAllColumns(skipHeader = false) {
            // MAKE SURE THIS WORKS FOR EITHER GRID??? or separate methods? (main one with control col, or display-only one without)
            const skipColIds = [controlColId];
            if (this.gridColumnApi) {
                const allColKeys = this.gridColumnApi.getAllColumns().map(col => col.getId()).filter(colId => !skipColIds.includes(colId));
                this.gridColumnApi.autoSizeColumns(allColKeys, skipHeader);
                console.log('column state after autoSizeAllColumns:', this.gridColumnApi.getColumnState());
            }
        },
        resetRowHeights() {
            // on the EDIT grid
            if (this.gridApi) {
                this.gridApi.resetRowHeights();
            }
        },
        dumpColDefs() {
            if (this.gridApi) {
                const allFinalColDefs = this.gridApi.getColumnDefs();
                console.log('all columnDefs:', allFinalColDefs);
            }
        },
        getDataColKeys(gridType) {
            // NOTE: must support both grid types: 'display' and 'edit' - they both use the same coldefs, but display ones have calculated cols
            // get all col keys that are actual data columns (not control col, etc.)
            // also skip any that are set as hide=True (permanently hidden in the UI, like an 'id' field)
            const skipColIds = [controlColId];
            const colApi = gridType === 'display' ? this.displayGridColumnApi : this.gridColumnApi;
            if (colApi) {
                // return this.gridColumnApi.getAllColumns().map(col => col.getId()).filter(colId => !skipColIds.includes(colId));
                return colApi.getAllColumns().filter(col => {
                    // filter out any cols that are hidden in the colDef, or in the skip list
                    const theColDef = col.getColDef();
                    if (skipColIds.includes(col.getId()) || theColDef?.hide === true) {
                        return false;
                    }
                    return true;
                }).map(col => col.getId());
            }
            return [];
        },
        dumpDiffRows() {
            // current rows might not have 'id' field if user added new rows, so we only compare existing rows
            const currentRowsById = this.currentPreppedValue ? Object.fromEntries(this.currentPreppedValue.filter(r => r.id).map(r => [r.id, r])) : {};
            const differentRowsStringified = [];
            const differentRowsIsEqual = [];
            const lastOutside = this.lastOutsidePreppedValue || {};
            for (const row of Object.values(lastOutside)) {
                const id = row.id;
                const initialRow = cloneDeep(row);
                const currentRow = id && currentRowsById[id] ? cloneDeep(currentRowsById[id]) : null;
                const isRowEqual = isEqual(initialRow, currentRow);
                if (!isRowEqual) {
                    differentRowsIsEqual.push({ initial: initialRow, current: currentRow });
                }
                const initialRowStringified = JSON.stringify(initialRow);
                const currentRowStringified = currentRow ? JSON.stringify(currentRow) : null;
                if (initialRowStringified !== currentRowStringified) {
                    differentRowsStringified.push({ initial: initialRow, current: currentRow });
                }
            }
            console.log('differentRowsStringified:', differentRowsStringified);
            console.log('differentRowsIsEqual:', differentRowsIsEqual);
            if (differentRowsIsEqual.length) {
                for (const diffobj of differentRowsIsEqual) {
                    const theDiff = detailedDiff(diffobj.initial, diffobj.current);
                    console.log('DIFF for row with id=', diffobj.initial.id, ':', theDiff);
                }
            }
            // NOTE: the download_url query params are different... why?  because we didn't reset changes properly... in setLocalValueFromOutside... we missed change
        },
        exportToExcel(gridType) {
            // NOTE - this should work for EITHER GRID - it uses data that is common to both, and uses the gridApi or displayGridApi depending on gridType passed in
            // https://www.ag-grid.com/archive/25.3.0/vue-grid/excel-export-api/#excelexportparams
            const exportParams = {
                author: 'Supplios',
                sheetName: 'SuppliosExport',
                fileName: 'supplios_export.xlsx',
                columnKeys: this.getDataColKeys(gridType),  // MUST SUPPORT BOTH GRID TYPES
                processCellCallback: (params) => {
                    // NOTE: this should be basically the same as in processCellForClipboard in control col... maybe refactor to a shared function???
                    // value is the raw value to copy from the grid - return the value you want to actually copy
                    // use our LiveGridCol to format the value since the grid doesn't do it properly in all cases (depends on parser, editor, etc.)
                    // TODO - might want to add to/from clipboard methods to LiveGridCol???  for dates, we should export to ISO string probably, but display as local date?
                    // dates... format as date format string in Excel....
                    // TODO - handle attachments better... or skip?
                    // TODO - add special functions for formatting to export to Excel, CSV, clipboard, on LiveGridCol.... for refData, you need to handle that yourself here
                    console.log('in processCellCallback for Excel export - params:', params);
                    const gridValue = params.value;
                    const fieldKey = params.column.colDef?.field;
                    if (!fieldKey) {
                        return params.value;  // no field... just return the value as-is... or should we return null???
                    }
                    const liveCol = this.liveGridColsByField[fieldKey];
                    return liveCol ? liveCol.prepValueForExcel(gridValue) : gridValue;
                }
            }
            if (gridType === 'display') {
                if (!this.displayGridApi) {
                    console.error('in exportToExcel - displayGridApi not ready yet, cannot export');
                    return;
                }
                this.displayGridApi.exportDataAsExcel(exportParams);  // use params to customize how its done... but your coldefs might handle it OK
            } else if (gridType === 'edit') {
                 if (!this.gridApi) {
                    console.error('in exportToExcel - gridApi not ready yet, cannot export');
                    return;
                }
                this.gridApi.exportDataAsExcel(exportParams);  // use params to customize how its done... but your coldefs might handle it OK
            } else {
                console.error('in exportToExcel - unknown gridType:', gridType);
            }
        },

        // DISPLAY GRID HANDLERS
        onDisplayGridSizeChanged(params) {
            console.log('_____ DISPLAY GRID SIZE CHANGED ______', params);
            // we can use this event to trigger things that need to happen when the display grid is actually visible on the page, since we render it lazily
            // for example, we can size columns to fit here, since we know the grid is visible and has a width to fit to (unlike on first data rendered, which can fire before grid is visible)
            if (params.api) {
                console.log('in onDisplayGridSizeChanged - sizing columns to fit then resetting row heights');
                params.api.sizeColumnsToFit();
                params.api.resetRowHeights();
            }
            // const displayGrid = this.$refs.displayGrid;
            // console.log('displayGrid width onDisplayGridSizeChanged?:', displayGrid?.$el ? displayGrid.$el.offsetWidth : 'N/A');
        },
        onDisplayGridReady(params) {
            console.log('_____ DISPLAY GRID READY ______', params);
            this.displayGridApi = params.api;
            this.displayGridColumnApi = params.columnApi;
        },
        onDisplayGridFirstDataRendered(params) {
            console.log('_____ DISPLAY GRID FIRST DATA RENDERED ______', params);
            // NOTE - this fires before we are even showing on the page (because we render forms lazily), so calling it here will actually make all the columns shrink to min widths... need to adjust again at a later event
            if (params.api) {
                console.log('in onDisplayGridFirstDataRendered - sizing columns to fit then resetting row heights');
                params.api.sizeColumnsToFit();
                params.api.resetRowHeights();
            }
        },
        onDisplayGridCellValueChanged(params) {
            // not sure we should do anything here... display grid is read-only, just let parent control it
            const field = params.colDef?.field;
            console.log(`DISPLAY GRID Cell value changed in field "${field}"`, params);
        },
        onDisplayGridColumnResized(params) {
            // look at the 'finished' flag in the params to avoid doing this multiple times during a resize action
            if (params.finished) {
                params.api.resetRowHeights();
            }
        },
        sizeDisplayGridColumnsToFit() {
            if (this.displayGridApi) {
                this.displayGridApi.sizeColumnsToFit();
            }
        },
        autoSizeAllDisplayColumns(skipHeader = false) {
            const skipColIds = [controlColId];
            if (this.displayGridColumnApi) {
                const allColKeys = this.displayGridColumnApi.getAllColumns().map(col => col.getId()).filter(colId => !skipColIds.includes(colId));
                this.displayGridColumnApi.autoSizeColumns(allColKeys, skipHeader);
            }
        },

        // EDIT MODAL HANDLERS
        killEditGrid() {
            // remove / reset things for the edit grid... need to call from many places
            this.gridApi = null;
            this.gridColumnApi = null;
            this.canShowEditGrid = false;
            this.editGridPopupParent = null;
        },
        startModalEdit() {
            this.editModalShowing = true;
            // put any more logic in the onEditModalShow/onEditModalShown handlers
        },
        onEditModalHidden() {
            // this.gridApi = null;
            // this.gridColumnApi = null;
            // this.editGridPopupParent = null;
            // this.canShowEditGrid = false;
            this.killEditGrid();  // do it in one place
            this.showModalCancelConfirm = false;
            // this.allowUpdateLocalFromOutside = true;  
        },
        onEditModalShow() {
            console.log('in onEditModalShow - modal is about to be shown - currentValue:', this.currentValue);
            // this.editGridPopupParent = this.$refs.editGridContainer;
            // this.currentValueBeforeModalEdit = null;  // reset this so we capture it again when grid loads
            // // and reset the providedRowData to currentValue (if set!) or the last outside value 
            // this.providedRowData = cloneDeep(this.lastOutsideValue || []);  // cloneDeep to be safe... we don't want to mess up the original data, that's shown in display grid
            // console.log('in onEditModalShow - setting providedRowData for edit grid to:', this.providedRowData);
            // we do NOT set canShowEditGrid here... we wait
        },
        onEditModalShown() {
            this.editGridPopupParent = this.$refs.editGridContainer;
            console.log('in onEditModalShown - modal is now shown - setting editGridPopupParent to:', this.editGridPopupParent);
            // set the initial data needed for edit grid
            this.currentValueBeforeModalEdit = null;  // reset this so we capture it again when grid loads
            // NOTE: do we need to do this next line?  lastOutsideValue and providedRowData should be the same at this point, right?  just decouple them to be safe????  already decoupled???
            this.providedRowData = cloneDeep(this.lastOutsideValue || []);  // cloneDeep to be safe... we don't want to mess up the original data, that's shown in display grid
            this.addMinRowsIfNeeded();  // make sure we have at least minRows
            console.log('in onEditModalShown - setting providedRowData for edit grid to:', this.providedRowData);
            // now we can show the grid... but giving it a tick to make sure the popup parent is set properly and all other stuff we just set is ready
            this.$nextTick(() => {
                console.log('in onEditModalShown - nextTick - now setting canShowEditGrid to true so we can show the edit grid in the modal');
                this.canShowEditGrid = true;
            });
        },
        onModalHeaderClose(event) {
            // user clicked the X button in the modal header (if we are still showing that...)
            // we cancel the default behavior, then check other things and proceed with the close if we can
            console.log('in onModalHeaderClose - user clicked X button to close modal');
            event.preventDefault();
            // TODO - check for changes, etc.
            this.editModalShowing = false;  // just close the modal... we don't save changes
        },
        requestModalCancel() {
            if (this.hasLocalChanges && !this.isReadOnly) {
                this.showModalCancelConfirm = true;
            } else {
                this.performModalCancel();
            }
        },
        abortModalCancel() {
            this.showModalCancelConfirm = false;
        },
        performModalCancel() {
            // hide the grid first, so we don't have it visible while we reset things and trigger grid events etc.
            // this.canShowEditGrid = false;
            // this.gridApi = null;
            // this.gridColumnApi = null;
            this.killEditGrid();  // do it in one place
            // TODO - maybe skip/change some of this if isReadOnly???  let's see....
            // we need to tell parent to re-validate things since we might have local changes that were never saved to form
            console.log('in performModalCancel - user confirmed they want to cancel edits and close modal - resetting to value before modal was opened:', this.currentValueBeforeModalEdit);
            this.handleDataChanges(this.currentValueBeforeModalEdit);  // pass in the value we had before the modal was opened, so we reset to that -- this sets this.currentValue also !!!
            // we must also set the providedRowData to that value, so the grid shows it properly... that's what it uses as its source of initial data
            this.providedRowData = Array.isArray(this.currentValueBeforeModalEdit) ? cloneDeep(this.currentValueBeforeModalEdit) : [];  // do we need to clone???  this is called infrequently, so should be fine
            // then we also must force parent to re-validate, since the handleDataChanges will likely not see any changes if we are going back to previous value, so it won't tell parent to do that
            this.context.performValidation();  // triggers parent to re-check our validation rule - it checks things in this.currentValue -- so it is important we set that above (we might not need this?)
            this.showModalCancelConfirm = false;
            this.editModalShowing = false;
            this.allowUpdateLocalFromOutside = true;  // we can now turn over control to outside again
        },

        // EDIT GRID EVENT HANDLERS
        onCellDoubleClicked(event) {
            // CAREFUL - this handles events from BOTH grids
            console.log('A cell was double-clicked', event);
        },
        onGridReady(params) {
            console.log(`_____ EDIT GRID READY on ${this.fieldLabel} ______`, params);
            this.gridApi = params.api;
            this.gridColumnApi = params.columnApi;

            // set validation 
            if (this.gridApi) {
                this.gridApi.forEachNode((node) => {
                    this.setValidationRowNode(node);
                });
            }
        },
        onFirstDataRendered(params) {
            console.log(`_____ EDIT GRID FIRST DATA RENDERED on ${this.fieldLabel} ______`, params);
            this.handleDataChanges();  // make sure to run change detection now that grid is loaded
            // this.autoSizeAllColumns();  // probably better to size to fit...
            this.sizeColumnsToFit();
            params.api.resetRowHeights();
            // CAPTURE THE 'before edit' VALUE FOR COMPARISON LATER -- and for resetting when the modal closes... 
            // we only do this if it was null before... otherwise maybe this got called multiple times???
            if (this.currentValueBeforeModalEdit === null) {
                const currentValueBeforeModalEdit = this.getCurrentGridData();  // must be NON-extracted data, since we need _isValid and _errors for error tracking
                this.currentValueBeforeModalEdit = cloneDeep(currentValueBeforeModalEdit);  // make sure to clone it so we don't accidentally mutate
            }
            // this.resetRowHeights();
            // this.allowUpdateLocalFromOutside = false;  // ??? now we want to ignore any changes from outside, until user clicks Save Edits or we are re-initialized
        },
        onCellValueChanged(params) {
            // to prevent major perf issues during paste, we suspend this during paste operations, then do it all in bulk when we are done
            // but we have to collect the rowNodeIds of the changed rows during the paste, so we know which ones to validate and update after the paste is done
            if (this.isPasteInProgress) {
                this.pastedRowIds.add(params.node.id);  // add the rowNodeId to our Set of pastedRowIds
                return;  // skip the rest of this handler during paste, we will handle it in onPasteEnd
            }
            const field = params.colDef?.field;
            // const colKey = params.column?.getColId();
            console.log(`Cell value changed in field "${field}" on ${this.fieldLabel}`, params);
            // has { value, newValue, oldValue, data, rowNode, colDef, column, rowIndex, rowPinned, api, columnApi, context, maybe more... }
            
            // NOTE: you might have very subtle changes depending on backend and cell types in editor... like changing from null to '' or similar...
            // const newValueStringified = JSON.stringify(params.newValue);
            // const oldValueStringified = JSON.stringify(params.oldValue);
            // if (newValueStringified === oldValueStringified) {
            //     console.log('in onCellValueChanged - newValue is SAME as oldValue');
            //     // return;  // no change, so don't do anything
            // } else {
            //     console.log('in onCellValueChanged - newValue is DIFFERENT than oldValue');
            //     console.log('NEW VALUE:', params.newValue);
            //     console.log('OLD VALUE:', params.oldValue);
            // }
            // value is the rawValue?  and newValue is after the parser/setter???  maybe... but something like that
            this.setValidationRowNode(params.node);
            this.handleDataChanges();
            // if (colKey) {  // REMOVING THIS... its quite annoying
            //     // might need to debounce this if doing a lot of edits at once???
            //     this.gridColumnApi.autoSizeColumn(colKey, true);  // true means skipHeader
            // }
        },
        onCellEditingStarted(params) {
            console.log('Cell editing started', params);
            this.isCellEditActive = true;
        },
        onCellEditingStopped(params) {
            console.log('Cell editing stopped', params);
            // this fires AFTER all our validation stuff, so its a good place to look for save requests and re-call those if needed
            if (this.saveRequestedAfterEdit) {
                console.log('in onCellEditingStopped - save was requested after edit, will now call startSaveToForm');
                this.saveRequestedAfterEdit = false;  // reset the flag
                this.startSaveToForm();
            }
            this.isCellEditActive = false;  // is this reliable??? what if multiple edits at once???  what if we remove a row that was editing?
        },
        onPasteStart(params) {
            console.log('Paste operation STARTED', params);
            this.isPasteInProgress = true;
            this.pastedRowIds = new Set();  // reset this to track the new paste operation - the rowNodeIds
        },
        onPasteEnd(params) {
            console.log('Paste operation ENDED', params);
            this.isPasteInProgress = false;
            // NOTE: we have to run all the things that we suspended during paste here... like validation, change detection, etc.  
            // we have to set validation on all the new rows then call handleDataChanges to update everything based on the new data... 
            // basically, everything we have in onCellValueChanged
            const pastedRowIds = Array.from(this.pastedRowIds);  // convert Set to Array for easier processing/logging
            if (pastedRowIds.length > 0) {
                // we had some pasted rows, so we need to validate those and then run change detection
                pastedRowIds.forEach(rowNodeId => {
                    const rowNode = this.gridApi.getRowNode(rowNodeId);
                    if (rowNode) {
                        this.setValidationRowNode(rowNode, false);  // set validation on the row, but we will do one big refresh at the end, so skip refreshing cells for each row
                    }
                });
                this.handleDataChanges();  // now that all pasted rows have their validation set, we can run change detection to update everything based on the new data
                this.gridApi.refreshCells({ force: true });  // now do one big refresh to update all the cells based on the new validation states and any other changes
                // also resize or auto-size?
                this.sizeColumnsToFit();  // don't do this, doesn't feel right for small copy/pastes - actually, its also annoying without it, so adding it back - maybe just resetRowHeights?
            } else {
                // no pasted rows?  just run change detection to be safe, but we likely don't need to do anything else since no data actually changed
                this.handleDataChanges();
            }
            this.pastedRowIds = new Set();  // reset this after handling the paste results
        },
        onGridModelUpdated(params) {
            console.log('The grid model was updated', params);
            // NOTE: might need to check for changes here too?  if on cell edits and row moves don't cover everything
        },
        onRowDataChanged(params) {
            console.log('The grid row data changed', params);
        },
        onRowDragEnd(params) {
            console.log('The row drag ended', params);
            this.handleDataChanges();
        },
        handleFilterChanged(params) {
            console.log('The grid filter changed', params);
        },
        handleColumnRowGroupChanged(params) {
            console.log('The grid column row group changed', params);
        },
        onRowGroupOpened(params) {
            console.log('A row group was opened', params);  
        },
        onColumnResized(params) {
            // console.log('A column was resized', params);
            // look at the 'finished' flag in the params to avoid doing this multiple times during a resize action
            if (params.finished) {
                params.api.resetRowHeights();
            }
        },
        onColumnVisible(params) {
            console.log('A column was made visible/invisible', params);
        },
        onColumnMoved(params) {
            console.log('A column was moved', params);
        },
        onGridColumnsChanged(params) {
            console.log('The grid columns changed', params);
        },
        onDisplayedColumnsChanged(params) {
            console.log('The grid displayed columns changed', params);
        },
    },
}
</script>

<style lang="scss">
.formulate-ag-grid-component-gridcontainer {
    /* NOTE: grid stuff in the modal will be outside the scope of the root container here */
    .ag-row {
        .ag-cell.control-col.row-valid {
            // background-color: var(--success-50) !important;   // light green
        }
        .ag-cell.control-col.row-invalid {
            background-color: var(--danger-50) !important;   // light red
        }
        .ag-cell.cell-invalid {
            background-color: var(--danger-50) !important;   // light red
            color: var(--danger-800) !important;  // dark red text
        }
        .ag-cell.cell-read-only {
            background-color: var(--gray-100) !important;  // light gray
            color: var(--gray-700) !important;  // medium gray text
        }
        // styles specifically for file renderer component
        .ag-cell div.grid-file-renderer {
            .grid-file-edit-button {
                display: none;
            }
            &:hover {
                .grid-file-edit-button {
                    display: block;
                }
            }
        }
    
        &.ag-row-hover {
            .ag-cell.control-col.row-valid {
                // background-color: var(--success-100) !important;   // slightly darker green on hover
            }
            .ag-cell.control-col.row-invalid {
                background-color: var(--danger-100) !important;   // slightly darker red on hover
            }
            .ag-cell.cell-invalid {
                background-color: var(--danger-100) !important;   // slightly darker red on hover
                color: var(--danger-900) !important;  // even darker red text on hover
            }
            .ag-cell.cell-read-only {
                background-color: var(--gray-200) !important;  // slightly darker gray on hover
                color: var(--gray-900) !important;  // darker gray text on hover
            }
        }

        &.ag-row-selected {
            // when we are editing, this class is added to the row that is being edited, so we can use it to style the row being edited
            .ag-cell.control-col.row-valid {
                background-color: var(--gray-200) !important;   // slightly darker gray to indicate edit mode, but keep the green indication of valid
            }
            .ag-cell.control-col.row-invalid {
                background-color: var(--danger-100) !important;   // keep the red indication of invalid, but maybe make it slightly lighter to indicate edit mode
            }
        }
    
    }
}
</style>